"use client"
import { useState, useRef, useEffect, useCallback } from "react"
import { ChevronDown, Search, Trash2, AudioLines, ArrowUp, Mic, Square, CirclePlus, X, CircleStop } from "lucide-react"
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { useAuth } from "../context/AuthContext"
import { auth } from "../components/Authenticator/firebase"
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth"
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { useSearchParams, useNavigate } from "react-router-dom"
import "katex/dist/katex.min.css"
import "highlight.js/styles/github.css"
import "../styles/thinking-styles.css"
import "../styles/dashboard.css"
// import ThinkingCollapse from "../components/ThinkingCollapse"
import VoiceAssistantModal from "../components/VoiceAssistantModal"
import { API_URL } from "../config/api"

// Icon Component
const Icon = ({ name, size = 20 }) => {
  const icons = {
    "bar-chart": (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="10" width="4" height="10" rx="1" />
        <rect x="10" y="6" width="4" height="14" rx="1" />
        <rect x="17" y="2" width="4" height="18" rx="1" />
      </svg>
    ),
    "trending-up": (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    ),
    "shopping-cart": (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
    ),
    package: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    "alert-triangle": (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
    search: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    bell: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
    "chevron-up": (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="18 15 12 9 6 15" />
      </svg>
    ),
    "chevron-down": (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    ),
  }
  return icons[name] || null
}

// KPI Card Component
const KPICard = ({ title, value, subtitle, trend, trendValue, color = "#111827" }) => (
  <div className="kpi-card">
    <div className="kpi-header">
      <span className="kpi-title">{title}</span>
      {trend && (
        <span className={`kpi-trend ${trend}`}>
          <Icon name={trend === "up" ? "chevron-up" : "chevron-down"} size={14} />
          {trendValue}
        </span>
      )}
    </div>
    <div className="kpi-value" style={{ color }}>
      {value}
    </div>
    {subtitle && (
      <div 
        className="kpi-subtitle" 
        style={typeof subtitle === 'string' && subtitle.includes('%') ? { color: '#10b981', fontSize: '0.75rem' } : {}}
      >
        {subtitle}
      </div>
    )}
  </div>
)

// Section Title Component
const SectionTitle = ({ icon, title, color = "#10b981" }) => (
  <div className="section-title" style={{ borderLeftColor: color }}>
    <span className="section-icon" style={{ color }}>
      <Icon name={icon} size={24} />
    </span>
    <h2>{title}</h2>
  </div>
)

// Line Chart Component usando Recharts con doble eje Y
const SalesLineChart = ({ data, selectedYears, yearColors }) => {
  // Transformar datos para Recharts con solo los años seleccionados
  const chartData = data.map((d) => {
    const item = { name: d.month };
    selectedYears.forEach(year => {
      item[`unidades${year}`] = d[`ventas${year}`] || 0;
      item[`montos${year}`] = d[`montos${year}`] || 0;
    });
    return item;
  });

  // Determinar si usar línea punteada (solo para años anteriores al más reciente)
  const maxYear = Math.max(...selectedYears);
  const getStrokeDasharray = (year, isMonto = false) => {
    if (isMonto) return '5 5'; // Montos siempre punteados
    if (year === maxYear) return '0'; // Línea sólida para unidades del año más reciente
    return '5 5'; // Línea punteada para unidades de años anteriores
  };

  // Formatear tooltip personalizado
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const yearData = {};
      payload.forEach(entry => {
        const year = entry.dataKey.replace(/unidades|montos/, '');
        if (!yearData[year]) yearData[year] = {};
        if (entry.dataKey.startsWith('unidades')) {
          yearData[year].unidades = entry.value;
        } else {
          yearData[year].montos = entry.value;
        }
      });

  return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#111827' }}>{label}</p>
          {Object.keys(yearData).sort((a, b) => b - a).map(year => {
            const data = yearData[year];
            const precioPromedio = data.unidades > 0 ? (data.montos / data.unidades).toFixed(2) : 0;
            return (
              <div key={year} style={{ marginBottom: '4px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  marginBottom: '2px'
                }}>
                  <span style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: yearColors[year] || '#6b7280',
                    display: 'inline-block'
                  }} />
                  <span style={{ fontWeight: 600, color: '#374151' }}>{year}:</span>
                </div>
                <div style={{ paddingLeft: '16px', fontSize: '0.875rem', color: '#6b7280' }}>
                  <div>Unidades: <strong style={{ color: '#111827' }}>{data.unidades?.toLocaleString() || 0}</strong></div>
                  <div>Monto: <strong style={{ color: '#111827' }}>€{data.montos?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</strong></div>
                  <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e5e7eb', color: '#10b981', fontWeight: 500 }}>
                    Precio promedio: <strong>€{precioPromedio}</strong>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: '100%', height: '350px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart
          data={chartData}
          margin={{
            top: 5,
            right: 50,
            left: 0,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 12, fill: '#6b7280' }}
            stroke="#9ca3af"
          />
          {/* Eje Y izquierdo - Unidades */}
          <YAxis 
            yAxisId="left"
            width={60}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            stroke="#9ca3af"
            label={{ 
              value: 'Unidades', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: '#6b7280', fontSize: '12px' }
            }}
          />
          {/* Eje Y derecho - Montos */}
          <YAxis 
            yAxisId="right"
            orientation="right"
            width={80}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            stroke="#9ca3af"
            label={{ 
              value: 'Montos (€)', 
              angle: 90, 
              position: 'insideRight',
              style: { textAnchor: 'middle', fill: '#6b7280', fontSize: '12px' }
            }}
            tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          {/* Líneas de unidades (sólidas) */}
          {selectedYears.sort((a, b) => b - a).map((year) => (
            <Line 
              key={`unidades${year}`}
              yAxisId="left"
              type="monotone" 
              dataKey={`unidades${year}`} 
              name={`${year} - Unidades`}
              stroke={yearColors[year] || '#6b7280'} 
              strokeWidth={2.5}
              strokeDasharray={getStrokeDasharray(year, false)}
              dot={{ r: 4, fill: yearColors[year] || '#6b7280' }}
              activeDot={{ r: 6 }}
          />
        ))}
          {/* Líneas de montos (punteadas) */}
          {selectedYears.sort((a, b) => b - a).map((year) => (
            <Line 
              key={`montos${year}`}
              yAxisId="right"
              type="monotone" 
              dataKey={`montos${year}`} 
              name={`${year} - Montos`}
              stroke={yearColors[year] || '#6b7280'} 
              strokeWidth={2.5}
              strokeDasharray="5 5"
              strokeOpacity={0.7}
              dot={{ r: 3, fill: yearColors[year] || '#6b7280', opacity: 0.7 }}
              activeDot={{ r: 5 }}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  )
}

// Horizontal Bar Chart Component
const HorizontalBarChart = ({ data, showCurrency = false }) => {
  const maxVal = Math.max(...data.map((d) => d.value), 1) // Evitar división por 0
  return (
    <div className="h-bar-chart">
      {data.map((item, idx) => (
        <div className="h-bar-row" key={idx}>
          <span className="h-bar-rank">{idx + 1}</span>
          <span className="h-bar-name">{item.name}</span>
          <div className="h-bar-track">
            <div
              className="h-bar-fill"
              style={{ width: `${(item.value / maxVal) * 100}%`, backgroundColor: item.color || "#10b981" }}
            />
          </div>
          <span className="h-bar-value">
            {showCurrency ? `$${item.value.toLocaleString()}` : item.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  )
}

// Donut Chart Component
const DonutChart = ({ data, centerValue, centerLabel }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0)
  let cumulative = 0
  const radius = 70
  const strokeWidth = 20
  const circumference = 2 * Math.PI * radius
  return (
    <div className="donut-wrapper">
      <div className="donut-container">
        <svg className="donut-svg" viewBox="0 0 180 180">
          {data.map((item, idx) => {
            const offset = (cumulative / total) * circumference
            const length = (item.value / total) * circumference
            cumulative += item.value
            return (
              <circle
                key={idx}
                cx="90"
                cy="90"
                r={radius}
                fill="none"
                stroke={item.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={-offset}
              />
            )
          })}
        </svg>
        <div className="donut-center">
          <span className="donut-value">{centerValue}</span>
          <span className="donut-label">{centerLabel}</span>
        </div>
      </div>
      <div className="donut-legend">
        {data.map((item, idx) => (
          <div className="legend-row" key={idx}>
            <span className="dot" style={{ backgroundColor: item.color }} />
            <span className="legend-name">{item.name}</span>
            <span className="legend-percent">{((item.value / total) * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Sample Data - inicializado con 12 meses para todos los años (2015-2025)
const initialSalesData = Array.from({ length: 12 }, (_, idx) => {
  const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  const data = { month: meses[idx] };
  for (let año = 2015; año <= 2025; año++) {
    data[`ventas${año}`] = 0;
  }
  return data;
})


const providers = [
  { name: "null", compras: 0, pedidos: 0, cumplimiento: 0 },
  { name: "null", compras: 0, pedidos: 0, cumplimiento: 0 },
  { name: "null", compras: 0, pedidos: 0, cumplimiento: 0 },
  { name: "null", compras: 0, pedidos: 0, cumplimiento: 0 },
]

// Estado para inventario (debe ser manejado con useState para que los cambios se reflejen)
const initialInventoryData = [
  { sku: "SKU-001", name: "null", stock: 0, minimo: 0, maximo: 0, estado: "ok" },
  { sku: "SKU-002", name: "null", stock: 0, minimo: 0, maximo: 0, estado: "bajo" },
  { sku: "SKU-003", name: "null", stock: 0, minimo: 0, maximo: 0, estado: "quiebre" },
  { sku: "SKU-004", name: "null", stock: 0, minimo: 0, maximo: 0, estado: "sobrestock" },
  { sku: "SKU-005", name: "null", stock: 0, minimo: 0, maximo: 0, estado: "ok" },
]

const alertsData = [
  { sku: "SKU-003", name: "null", stock: 0, diasQuiebre: 0, ventasDiarias: 0, riesgo: "quiebre" },
  { sku: "SKU-002", name: "null", stock: 0, diasCobertura: 0, ventasDiarias: 0, riesgo: "bajo" },
  { sku: "SKU-008", name: "null", stock: 0, diasCobertura: 0, ventasDiarias: 0, riesgo: "bajo" },
  { sku: "SKU-004", name: "null", stock: 0, exceso: 0, costoExceso: 0, riesgo: "sobrestock" },
]

const categoryData = [
  { name: "Computadoras", value: 0, color: "#10b981" },
  { name: "Periféricos", value: 0, color: "#10b981" },
  { name: "Smartphones", value: 0, color: "#10b981" },
  { name: "Audio", value: 0, color: "#10b981" },
  { name: "Otros", value: 0, color: "#6b7280" },
]

const KPIArticulos = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [message, setMessage] = useState("")
  const [chatMessages, setChatMessages] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [currentConversationId, setCurrentConversationId] = useState(null)
  const messagesEndRef = useRef(null)
  const chatContainerRef = useRef(null)
  const mainContentRef = useRef(null)
  const [isMobile, setIsMobile] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)

  // Agregar después de los otros estados
  const [activeSection, setActiveSection] = useState("artefactos")
  const [historialExpanded, setHistorialExpanded] = useState(true)
  const [proyectosExpanded, setProyectosExpanded] = useState(false)
  
  // Estado para importe total 2025
  const [importeTotal2025, setImporteTotal2025] = useState(0)
  const [loadingImporteTotal, setLoadingImporteTotal] = useState(true)
  const [porcentajeCambioImporte, setPorcentajeCambioImporte] = useState(0)
  
  // Estado para artículos vendidos
  const [articulosVendidos, setArticulosVendidos] = useState(0)
  const [loadingArticulosVendidos, setLoadingArticulosVendidos] = useState(true)
  const [porcentajeCambio, setPorcentajeCambio] = useState(0)
  
  // Estado para compras totales
  const [comprasTotales, setComprasTotales] = useState(0)
  const [loadingComprasTotales, setLoadingComprasTotales] = useState(true)
  const [porcentajeCambioCompras, setPorcentajeCambioCompras] = useState(0)
  
  // Estado para Top 5 productos
  const [topProductosSueltos, setTopProductosSueltos] = useState([
    { name: "null", value: 0, color: "#10b981" },
    { name: "null", value: 0, color: "#10b981" },
    { name: "null", value: 0, color: "#10b981" },
    { name: "null", value: 0, color: "#10b981" },
    { name: "null", value: 0, color: "#10b981" },
  ])
  const [topProductosBandejas, setTopProductosBandejas] = useState([
    { name: "null", value: 0, color: "#3b82f6" },
    { name: "null", value: 0, color: "#3b82f6" },
    { name: "null", value: 0, color: "#3b82f6" },
    { name: "null", value: 0, color: "#3b82f6" },
    { name: "null", value: 0, color: "#3b82f6" },
  ])
  const [, setLoadingTopProductos] = useState(true)

  // Estado para evolución mensual de ventas
  const [salesData, setSalesData] = useState(initialSalesData)
  const [loadingEvolucionVentas, setLoadingEvolucionVentas] = useState(true)
  
  // Estado para años seleccionados (inicialmente 2024 y 2025)
  const [selectedYears, setSelectedYears] = useState([2024, 2025])
  
  // Estado para mostrar/ocultar el dropdown de años
  const [showYearFilter, setShowYearFilter] = useState(false)
  
  // Estado para ventas del mes actual
  const [ventasMesActual, setVentasMesActual] = useState(0)
  const [loadingVentasMes, setLoadingVentasMes] = useState(true)
  const [porcentajeCambioVentasMes, setPorcentajeCambioVentasMes] = useState(0)
  
  // Estado para unidades vendidas del mes actual
  const [unidadesMesActual, setUnidadesMesActual] = useState(0)
  const [loadingUnidadesMes, setLoadingUnidadesMes] = useState(true)
  
  // Estado para inventario
  const [inventoryData] = useState(initialInventoryData)
  
  // Estado para filtrar por estado en la tabla
  const [filtroEstado, setFiltroEstado] = useState(null) // null = mostrar todos
  
  // Colores para cada año
  const yearColors = {
    2025: '#10b981', // Verde
    2024: '#3b82f6', // Azul
    2023: '#f59e0b', // Naranja
    2022: '#ef4444', // Rojo
    2021: '#8b5cf6', // Púrpura
    2020: '#ec4899', // Rosa
    2019: '#06b6d4', // Cyan
    2018: '#84cc16', // Lima
    2017: '#f97316', // Naranja oscuro
    2016: '#6366f1', // Índigo
    2015: '#14b8a6', // Teal
  }
  
  // Ref para el dropdown
  const yearFilterRef = useRef(null)
  
  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (yearFilterRef.current && !yearFilterRef.current.contains(event.target)) {
        setShowYearFilter(false)
      }
    }
    
    if (showYearFilter) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showYearFilter])

  // Eliminar la línea donde se declara historialExpanded:
  // const [historialExpanded, setHistorialExpanded] = useState(false)

  // Agregar un nuevo estado para controlar el menú del usuario:
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [modalView, setModalView] = useState("main") // 'main', 'admin', 'appearance', etc.

  // Estado para el modal de búsqueda
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")
  
  // Estados para asistente de voz multimodal con OpenAI (Modal)
  const [voiceModalOpen, setVoiceModalOpen] = useState(false)

  // Estados para el drag del bottom sheet en móvil
  const [isDragging, setIsDragging] = useState(false)
  const [dragY, setDragY] = useState(0)
  const [startY, setStartY] = useState(0)

  // Estados para el drag del search bottom sheet
  const [isSearchDragging, setIsSearchDragging] = useState(false)
  const [searchDragY, setSearchDragY] = useState(0)
  const [searchStartY, setSearchStartY] = useState(0)

  // Estados para grabación de audio
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [isTranscriptionPaused, setIsTranscriptionPaused] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  // eslint-disable-next-line no-unused-vars
  const [audioChunks, setAudioChunks] = useState([])

  // Ref para controlar el streaming y poder cancelarlo
  const streamReaderRef = useRef(null)
  const streamIntervalRef = useRef(null)
  const shouldStopStreamingRef = useRef(false)
  // const currentAudioRef = useRef(null)
  // const audioAbortControllerRef = useRef(null) // Para abortar peticiones de audio
  // const isPlayingAudioRef = useRef(false) // Usar ref en lugar de state para bloqueo inmediato
  // const [isPlayingAudio, setIsPlayingAudio] = useState(false)

  // Obtener la función de logout del contexto de autenticación
  const { logout, user } = useAuth()

  // Estados para el perfil de usuario
  const [profileName, setProfileName] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [currentPassword, setCurrentPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
  const [profileError, setProfileError] = useState("")
  const [profileSuccess, setProfileSuccess] = useState("")
  const fileInputRef = useRef(null)

  // Función para obtener las iniciales del usuario
  const getUserInitials = () => {
    if (!user?.displayName) return "U"
    
    const names = user.displayName.split(" ")
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase()
    }
    return names[0][0].toUpperCase()
  }

  // Función para obtener la imagen del avatar o las iniciales
  const getUserAvatar = () => {
    if (user?.photoURL) {
      return <img src={user.photoURL} alt="Avatar" className="ds-avatar-image" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} />
    }
    return <span>{getUserInitials()}</span>
  }

  // Función para obtener solo el primer nombre del usuario
  // const getUserFirstName = () => {
  //   if (!user?.displayName) return "Usuario"
  //   
  //   const names = user.displayName.split(" ")
  //   return names[0]
  // }

  // Datos de ejemplo para el historial de chats
  const [chatHistory, setChatHistory] = useState([])

  // Sincronizar datos del usuario con estados locales
  useEffect(() => {
    if (user) {
      setProfileName(user.displayName || "")
      // La imagen se manejará desde user.photoURL
    }
  }, [user])

  // Agregar estado para forzar la actualización de las fechas
  const [timeUpdate, setTimeUpdate] = useState(0)

  // Estados para paginación del historial
  const [historialItemsToShow, setHistorialItemsToShow] = useState(10) // Mostrar 10 inicialmente
  const HISTORIAL_INCREMENT = 10 // Cargar 10 más cada vez

  // Función para cargar historial
  const loadChatHistory = useCallback(async () => {
      try {
        const token = await auth.currentUser?.getIdToken()
        if (!token) return

      const response = await fetch(`${API_URL}/api/chat/conversations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error(`Error al cargar el historial: ${response.status}`)
        }

        const data = await response.json()
        if (data.success) {
          setChatHistory(data.data)
        } else {
          throw new Error(data.error || "Error al cargar el historial de chats")
        }
      } catch (error) {
        console.error("Error al cargar el historial de chats:", error)
      }
  }, [])

  // Función para actualizar títulos genéricos
  const updateGenericTitles = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(`${API_URL}/api/chat/conversations/update-titles`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.updatedCount > 0) {
          console.log(`✅ [FRONTEND] ${data.updatedCount} títulos actualizados`)
          // Recargar el historial después de actualizar títulos
          return true
        }
      }
      return false
    } catch (error) {
      console.error("Error actualizando títulos:", error)
      return false
    }
  }, [])

  // Cargar el historial de chats al montar el componente
  useEffect(() => {
    const initializeChatHistory = async () => {
      // Primero actualizar títulos genéricos
      await updateGenericTitles()
      // Luego cargar el historial
      await loadChatHistory()
    }

    initializeChatHistory()
  }, [loadChatHistory, updateGenericTitles])

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
      if (window.innerWidth > 768) {
        setMobileSidebarOpen(false)
      }
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Cargar importe total 2025
  useEffect(() => {
    const fetchImporteTotal = async () => {
      try {
        setLoadingImporteTotal(true)
        
        const token = await auth.currentUser?.getIdToken()
        if (!token) {
          throw new Error('No hay usuario autenticado')
        }
        
        const response = await fetch(`${API_URL}/api/export/importe-total-2025`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          console.log('📊 Importe total recibido:', data)
          
          if (data.total !== null && data.total !== undefined) {
            const total = Number(data.total) || 0
            console.log('📊 Total importe 2025:', total)
            setImporteTotal2025(total)
            
            if (data.porcentajeCambio !== null && data.porcentajeCambio !== undefined) {
              setPorcentajeCambioImporte(Number(data.porcentajeCambio) || 0)
            }
          } else {
            console.warn('⚠️ No se encontró el total de importe en la respuesta')
            setImporteTotal2025(0)
            setPorcentajeCambioImporte(0)
          }
        }
      } catch (error) {
        console.error('Error al cargar importe total:', error)
        setImporteTotal2025(0)
        setPorcentajeCambioImporte(0)
      } finally {
        setLoadingImporteTotal(false)
      }
    }

    fetchImporteTotal()
  }, [])

  // Cargar artículos vendidos
  useEffect(() => {
    const fetchArticulosVendidos = async () => {
      try {
        setLoadingArticulosVendidos(true)
        
        const token = await auth.currentUser?.getIdToken()
        if (!token) {
          throw new Error('No hay usuario autenticado')
        }
        
        const response = await fetch(`${API_URL}/api/export/articulos-vendidos-2025`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error('Error al obtener artículos vendidos')
        }

        const data = await response.json()
        console.log('📊 Datos recibidos del servidor:', data)
        
        if (data.total !== null && data.total !== undefined) {
          const total = Number(data.total) || 0
          console.log('📊 Total de artículos vendidos:', total)
          setArticulosVendidos(total)
          
          if (data.porcentajeCambio !== null && data.porcentajeCambio !== undefined) {
            setPorcentajeCambio(Number(data.porcentajeCambio) || 0)
          }
        } else {
          console.warn('⚠️ No se encontró el total en la respuesta')
          setArticulosVendidos(0)
          setPorcentajeCambio(0)
        }
      } catch (error) {
        console.error('Error al cargar artículos vendidos:', error)
        setArticulosVendidos(0)
      } finally {
        setLoadingArticulosVendidos(false)
      }
    }

    fetchArticulosVendidos()
  }, [])

  // Cargar compras totales
  useEffect(() => {
    const fetchComprasTotales = async () => {
      try {
        setLoadingComprasTotales(true)
        
        const token = await auth.currentUser?.getIdToken()
        if (!token) {
          throw new Error('No hay usuario autenticado')
        }
        
        const response = await fetch(`${API_URL}/api/export/compras-totales-2025`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error('Error al obtener compras totales')
        }

        const data = await response.json()
        console.log('📊 Datos de compras recibidos del servidor:', data)
        
        if (data.total !== null && data.total !== undefined) {
          const total = Number(data.total) || 0
          console.log('📊 Total de compras:', total)
          setComprasTotales(total)
          
          if (data.porcentajeCambio !== null && data.porcentajeCambio !== undefined) {
            setPorcentajeCambioCompras(Number(data.porcentajeCambio) || 0)
          }
        } else {
          console.warn('⚠️ No se encontró el total de compras en la respuesta')
          setComprasTotales(0)
          setPorcentajeCambioCompras(0)
        }
      } catch (error) {
        console.error('Error al cargar compras totales:', error)
        setComprasTotales(0)
        setPorcentajeCambioCompras(0)
      } finally {
        setLoadingComprasTotales(false)
      }
    }

    fetchComprasTotales()
  }, [])

  // Cargar Top 5 productos
  useEffect(() => {
    const fetchTopProductos = async () => {
      try {
        setLoadingTopProductos(true)
        
        const token = await auth.currentUser?.getIdToken()
        if (!token) {
          throw new Error('No hay usuario autenticado')
        }
        
        // Cargar Top 5 sueltos
        const responseSueltos = await fetch(`${API_URL}/api/export/top5-articulos-sueltos`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        // Cargar Top 5 bandejas
        const responseBandejas = await fetch(`${API_URL}/api/export/top5-articulos-bandejas`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (responseSueltos.ok && responseBandejas.ok) {
          const dataSueltos = await responseSueltos.json()
          const dataBandejas = await responseBandejas.json()
          
          console.log('📊 Top 5 sueltos recibidos:', dataSueltos)
          console.log('📊 Top 5 bandejas recibidos:', dataBandejas)
          
          // Formatear datos para sueltos
          const sueltosFormateados = dataSueltos.productos.map((item) => ({
            name: item.denominacion_articulo || 'Sin nombre',
            value: Number(item.unidades_vendidas || 0),
            color: "#10b981"
          }))
          
          // Rellenar hasta 5 elementos si hay menos
          while (sueltosFormateados.length < 5) {
            sueltosFormateados.push({ name: "null", value: 0, color: "#10b981" })
          }
          
          // Formatear datos para bandejas
          const bandejasFormateados = dataBandejas.productos.map((item) => ({
            name: item.denominacion_articulo || 'Sin nombre',
            value: Number(item.unidades_vendidas || 0),
            color: "#3b82f6"
          }))
          
          // Rellenar hasta 5 elementos si hay menos
          while (bandejasFormateados.length < 5) {
            bandejasFormateados.push({ name: "null", value: 0, color: "#3b82f6" })
          }
          
          setTopProductosSueltos(sueltosFormateados.slice(0, 5))
          setTopProductosBandejas(bandejasFormateados.slice(0, 5))
        }
      } catch (error) {
        console.error('Error al cargar top productos:', error)
      } finally {
        setLoadingTopProductos(false)
      }
    }

    fetchTopProductos()
  }, [])

  // Cargar evolución mensual de ventas
  useEffect(() => {
    const fetchEvolucionVentas = async () => {
      try {
        setLoadingEvolucionVentas(true)
        
        const token = await auth.currentUser?.getIdToken()
        if (!token) {
          throw new Error('No hay usuario autenticado')
        }
        
        const response = await fetch(`${API_URL}/api/export/evolucion-ventas-mensual`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          
          console.log('📊 Evolución mensual recibida:', data)
          
          if (data.datos && data.datos.length > 0) {
            setSalesData(data.datos)
          }
        }
      } catch (error) {
        console.error('Error al cargar evolución mensual:', error)
      } finally {
        setLoadingEvolucionVentas(false)
      }
    }

    fetchEvolucionVentas()
  }, [])

  // Cargar ventas del mes actual
  useEffect(() => {
    const fetchVentasMes = async () => {
      try {
        setLoadingVentasMes(true)
        
        const token = await auth.currentUser?.getIdToken()
        if (!token) {
          throw new Error('No hay usuario autenticado')
        }
        
        const response = await fetch(`${API_URL}/api/export/ventas-mes-actual`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          console.log('📊 Ventas del mes actual:', data)
          setVentasMesActual(data.monto || 0)
          if (data.porcentajeCambio !== null && data.porcentajeCambio !== undefined) {
            setPorcentajeCambioVentasMes(Number(data.porcentajeCambio) || 0)
          }
        }
      } catch (error) {
        console.error('Error al cargar ventas del mes:', error)
      } finally {
        setLoadingVentasMes(false)
      }
    }

    fetchVentasMes()
  }, [])

  // Cargar unidades vendidas del mes actual
  useEffect(() => {
    const fetchUnidadesMes = async () => {
      try {
        setLoadingUnidadesMes(true)
        
        const token = await auth.currentUser?.getIdToken()
        if (!token) {
          throw new Error('No hay usuario autenticado')
        }
        
        const response = await fetch(`${API_URL}/api/export/unidades-mes-actual`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          console.log('📊 Unidades del mes actual:', data)
          setUnidadesMesActual(data.unidades || 0)
        }
      } catch (error) {
        console.error('Error al cargar unidades del mes:', error)
      } finally {
        setLoadingUnidadesMes(false)
      }
    }

    fetchUnidadesMes()
  }, [])

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen(!mobileSidebarOpen)
    } else {
      // Si el sidebar se va a cerrar, colapsar proyectos
      if (sidebarOpen) {
        setProyectosExpanded(false)
      }
      setSidebarOpen(!sidebarOpen)
    }
  }

  // Funciones para manejo del perfil
  const handleImageSelect = () => {
    fileInputRef.current?.click()
  }

  const handleImageUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setProfileError("Por favor selecciona un archivo de imagen válido")
      return
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setProfileError("La imagen debe ser menor a 5MB")
      return
    }

    try {
      setIsUpdatingProfile(true)
      setProfileError("")

      // Inicializar Firebase Storage
      const storage = getStorage()
      const imageRef = ref(storage, `profile-images/${user.uid}/${Date.now()}-${file.name}`)

      // Subir imagen
      const snapshot = await uploadBytes(imageRef, file)
      const downloadURL = await getDownloadURL(snapshot.ref)

      // Actualizar perfil del usuario
      await updateProfile(user, {
        photoURL: downloadURL
      })

      setProfileSuccess("Imagen actualizada exitosamente")
      setTimeout(() => setProfileSuccess(""), 3000)

    } catch (error) {
      console.error("Error al subir imagen:", error)
      setProfileError("Error al subir la imagen. Intenta de nuevo.")
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  const handleUpdateName = async () => {
    if (!profileName.trim()) {
      setProfileError("El nombre no puede estar vacío")
      return
    }

    try {
      setIsUpdatingProfile(true)
      setProfileError("")

      await updateProfile(user, {
        displayName: profileName.trim()
      })

      setProfileSuccess("Nombre actualizado exitosamente")
      setTimeout(() => setProfileSuccess(""), 3000)

    } catch (error) {
      console.error("Error al actualizar nombre:", error)
      setProfileError("Error al actualizar el nombre. Intenta de nuevo.")
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword) {
      setProfileError("Debes completar ambos campos de contraseña")
      return
    }

    if (newPassword.length < 6) {
      setProfileError("La nueva contraseña debe tener al menos 6 caracteres")
      return
    }

    try {
      setIsUpdatingProfile(true)
      setProfileError("")

      // Reautenticar usuario
      const credential = EmailAuthProvider.credential(user.email, currentPassword)
      await reauthenticateWithCredential(user, credential)

      // Actualizar contraseña
      await updatePassword(user, newPassword)

      setNewPassword("")
      setCurrentPassword("")
      setProfileSuccess("Contraseña actualizada exitosamente")
      setTimeout(() => setProfileSuccess(""), 3000)

    } catch (error) {
      console.error("Error al actualizar contraseña:", error)
      if (error.code === 'auth/wrong-password') {
        setProfileError("La contraseña actual es incorrecta")
      } else {
        setProfileError("Error al actualizar la contraseña. Intenta de nuevo.")
      }
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  const handleSaveChanges = async () => {
    setProfileError("")
    setProfileSuccess("")

    // Actualizar nombre si cambió
    if (profileName.trim() !== user?.displayName) {
      await handleUpdateName()
    }

    // Actualizar contraseña si se proporcionó
    if (newPassword && currentPassword) {
      await handleUpdatePassword()
    }

    if (profileName.trim() === user?.displayName && !newPassword) {
      setProfileError("No hay cambios para guardar")
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      console.log("Sesión cerrada exitosamente")
    } catch (error) {
      console.error("Error al cerrar sesión:", error)
    }
  }

  // Cargar la conversación desde la URL al montar el componente
  useEffect(() => {
    const conversationId = searchParams.get("chat")
    if (conversationId && !conversationId.startsWith("temp_")) {
      // Validar que el chat pertenece al usuario antes de cargarlo
      const validateAndLoadChat = async () => {
        try {
          const token = await auth.currentUser?.getIdToken()
          if (!token) return

          // Intentar cargar los mensajes del chat (esto ya valida la pertenencia en el backend)
          const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}/messages`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          if (response.ok) {
            // Si la respuesta es exitosa, el chat pertenece al usuario
            setCurrentConversationId(conversationId)
          } else if (response.status === 404 || response.status === 403) {
            // Si el chat no existe o no pertenece al usuario, limpiar la URL
            console.warn("Chat no encontrado o no pertenece al usuario:", conversationId)
            setSearchParams({}) // Limpiar parámetros de URL
            setCurrentConversationId(null)
            setChatMessages([])
          } else {
            console.error("Error al validar el chat:", response.status)
          }
        } catch (error) {
          console.error("Error al validar la conversación:", error)
          // En caso de error, limpiar la URL para evitar estados inconsistentes
          setSearchParams({})
          setCurrentConversationId(null)
          setChatMessages([])
        }
      }

      validateAndLoadChat()
    }
  }, [searchParams, setSearchParams])

  const handleConversationClick = async (conversationId) => {
    // Navegar a la página de chat con la conversación seleccionada
    navigate(`/chat?conversation=${conversationId}`)
  }

  const handleNewChat = () => {
    // Navegar a la página de chat para iniciar una nueva conversación
    navigate("/chat")
  }


  // Función para sincronizar memoria RAM con Firebase
  const syncRAMWithFirebase = useCallback(async (conversationId, firebaseMessages) => {
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      // Enviar mensajes a la memoria RAM del backend
      const response = await fetch(`${API_URL}/api/chat/sync-ram`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversationId,
          messages: firebaseMessages
        }),
      })

      if (response.ok) {
        console.log(`🔄 [FRONTEND] Memoria RAM sincronizada con Firebase para: ${conversationId}`)
      }
    } catch (error) {
      console.error("Error sincronizando memoria RAM:", error)
    }
  }, [])

  const loadConversationMessages = useCallback(async (conversationId) => {
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}/messages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Error al cargar los mensajes: ${response.status}`)
      }

      const data = await response.json()
      if (data.success) {
        // Convertir los mensajes al formato que espera el componente
        const formattedMessages = data.messages.map((msg, index) => ({
          id: Date.now() + index,
          text: msg.content,
          sender: msg.role === "user" ? "user" : "bot",
        }))
        setChatMessages(formattedMessages)
        console.log(`📖 [FRONTEND] Cargados ${formattedMessages.length} mensajes de conversación: ${conversationId}`)
        
        // Sincronizar memoria RAM con Firebase
        await syncRAMWithFirebase(conversationId, data.messages)
      } else {
        throw new Error(data.error || "Error al cargar los mensajes")
      }
    } catch (error) {
      console.error("Error al cargar los mensajes:", error)
      setChatMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: "Hubo un error al cargar los mensajes. Por favor, intenta de nuevo.",
          sender: "bot",
          isError: true,
        },
      ])
    }
  }, [syncRAMWithFirebase])

  // Modificar el useEffect para cargar los mensajes cuando cambia la conversación
  useEffect(() => {
    if (currentConversationId && !currentConversationId.startsWith("temp_")) {
      loadConversationMessages(currentConversationId)
    }
  }, [currentConversationId, loadConversationMessages])


  // NOTA: Todas las funciones de voz ahora están en VoiceAssistantModal.jsx


  // Funciones para grabación de audio con OpenAI Whisper
  const startRecording = async () => {
    try {
      console.log('🎤 Iniciando grabación de audio...');
      
      // Solicitar permisos de micrófono
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });
      
      // Crear MediaRecorder
      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      const chunks = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = async () => {
        console.log('🛑 Grabación detenida, procesando audio...');
        
        // Crear blob del audio
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        
        // Detener el stream
        stream.getTracks().forEach(track => track.stop());
        
        // Transcribir audio
        await transcribeAudio(audioBlob);
        
        // Limpiar chunks
        setAudioChunks([]);
      };
      
      // Iniciar grabación
      recorder.start();
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
      
      console.log('✅ Grabación iniciada');
      
    } catch (error) {
      console.error('❌ Error al iniciar grabación:', error);
      alert('Error al acceder al micrófono. Verifica los permisos.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      console.log('🛑 Deteniendo grabación...');
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    try {
      setIsTranscribing(true);
      console.log('🔄 Enviando audio a Whisper API...');
      console.log('🔄 [FRONTEND] Tamaño del audio:', audioBlob.size, 'bytes');
      console.log('🔄 [FRONTEND] Tipo del audio:', audioBlob.type);
      console.log('🔄 [FRONTEND] Usuario actual:', user?.uid);
      console.log('🔄 [FRONTEND] Token disponible:', !!user?.accessToken);
      
      // Crear FormData
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      formData.append('model', 'whisper-1');
      formData.append('language', 'es'); // Español
      formData.append('response_format', 'text');
      
      // Enviar a OpenAI Whisper API
      const response = await fetch(`${API_URL}/api/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.accessToken}` // Usar token del usuario
        },
        body: formData
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { error: await response.text() };
        }
        console.error('❌ [FRONTEND] Error en transcripción:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Error en transcripción: ${response.status} - ${errorData.error || 'Error desconocido'}`);
      }
      
      const responseData = await response.json();
      const transcribedText = responseData.text;
      console.log('✅ Audio transcrito:', transcribedText);
      console.log('📊 Detalles de transcripción:', {
        duración: responseData.duration + 'ms',
        tamañoArchivo: responseData.fileSize + ' bytes'
      });
      
      // Establecer el texto transcrito en el input
      const cleanText = transcribedText.trim();
      setMessage(prev => {
        // Si ya hay texto, agregarlo al final
        if (prev.trim()) {
          return prev + ' ' + cleanText;
        } else {
          return cleanText;
        }
      });
      
      // Mostrar mensaje de confirmación
      console.log('🎤 Transcripción completada. El texto está listo para enviar.');
      console.log('📝 Texto transcrito:', cleanText);
      
      // Enfocar el input para que el usuario pueda editar si quiere
      setTimeout(() => {
        const input = document.querySelector('.ds-chat-input');
        if (input) {
          input.focus();
          // Colocar el cursor al final del texto completo
          const fullText = input.value;
          input.setSelectionRange(fullText.length, fullText.length);
        }
      }, 100);
      
    } catch (error) {
      console.error('❌ Error en transcripción:', error);
      alert('Error al transcribir el audio. Intenta de nuevo.');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Funciones para los iconos de interacción de mensajes
  /* const handleRegenerateResponse = async (messageId) => {
    console.log('🔄 Regenerando respuesta para mensaje:', messageId);
    
    // Encontrar el índice del mensaje a regenerar
    const messageIndex = chatMessages.findIndex(msg => msg.id === messageId);
    if (messageIndex === -1) {
      console.error('❌ No se encontró el mensaje');
      return;
    }
    
    // Buscar el mensaje del usuario anterior
    let userMessageIndex = -1;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (chatMessages[i].sender === 'user') {
        userMessageIndex = i;
        break;
      }
    }
    
    if (userMessageIndex === -1) {
      console.error('❌ No se encontró el mensaje del usuario');
      return;
    }
    
    const userMessage = chatMessages[userMessageIndex];
    console.log('📝 Regenerando respuesta para:', userMessage.text);
    
    // Eliminar el mensaje de la IA actual
    setChatMessages(prev => prev.filter(msg => msg.id !== messageId));
    
    // Crear nuevo mensaje del bot con estado de streaming
    const botMessage = {
      id: Date.now(),
      text: "",
      sender: "bot",
      isStreaming: true,
    };
    
    setChatMessages(prev => [...prev, botMessage]);
    setIsTyping(true);
    
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("No hay usuario autenticado");
      }
      
      const response = await fetch(`${API_URL}/api/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          message: userMessage.text,
          conversationId: currentConversationId,
          image: null
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.status}`);
      }
      
      console.log("✅ [FRONTEND] Conexión de streaming establecida para regenerar");

      // Variables para streaming natural
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = "";
      let buffer = "";
      let streamingInterval = null;

      // Función para mostrar el buffer acumulado de forma suave
      const flushBuffer = () => {
        if (buffer.trim()) {
          fullResponse += buffer;
          
          // Capturar variables para evitar problemas de closure
          const msgId = botMessage.id;
          const currentResponse = fullResponse;
          
          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === msgId
                ? {
                    ...msg,
                    text: currentResponse,
                    isStreaming: true,
                  }
                : msg,
            ),
          );

          console.log("📝 [FRONTEND] Mostrando buffer:", buffer.trim());
          buffer = "";
        }
      };

      // Intervalo para mostrar contenido de forma muy fluida (cada 30ms)
      streamingInterval = setInterval(() => {
        flushBuffer(); // Mostrar cualquier contenido pendiente cada 30ms
      }, 30);

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log("🏁 [FRONTEND] Stream de regeneración completado");
            break;
          }

          // Decodificar el chunk
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n').filter(line => line.trim());

          for (const line of lines) {
            try {
              const data = JSON.parse(line);
              
              if (data.type === 'chunk' && data.content) {
                // Filtrar contenido válido y agregarlo al buffer
                const content = data.content;
                
                // Solo agregar contenido que no sea vacío o caracteres extraños
                if (content && content.trim()) {
                  buffer += content;
                } else if (content === ' ' || content === '\n') {
                  // Preservar espacios y saltos de línea
                  buffer += content;
                }
                
              } else if (data.type === 'status_update') {
                // Manejar actualizaciones de estado (ROJO)
                console.log("🔴 [FRONTEND] STATUS_UPDATE recibido:", data);
                console.log("🔍 [FRONTEND] Scope recibido del backend:", data.scope);
                const msgId = botMessage.id;
                const finalScope = data.scope || 'semillero';
                console.log("✅ [FRONTEND] Scope final que se guardará:", finalScope);
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === msgId
                      ? {
                          ...msg,
                          statusUpdate: data.content,
                          statusUpdateType: 'status_update_spec',
                          isStreaming: true,
                          // Capturar scope REAL del backend (huerto/semillero/eco/multiple)
                          detectedScope: finalScope,
                        }
                      : msg,
                  ),
                );
                
              } else if (data.type === 'summary') {
                // Manejar resúmenes (VERDE)
                console.log("🟢 [FRONTEND] SUMMARY recibido:", data);
                const msgId = botMessage.id;
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === msgId
                      ? {
                          ...msg,
                          summary: data.content,
                          summaryType: 'summary_spec',
                        }
                      : msg,
                  ),
                );
                
              } else if (data.type === 'completion') {
                // Manejar completion tracking (AZUL)
                console.log("🔵 [FRONTEND] COMPLETION recibido:", data);
                const msgId = botMessage.id;
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === msgId
                      ? {
                          ...msg,
                          completion: data.content,
                          completionType: 'completion_spec',
                        }
                      : msg,
                  ),
                );
                
              } else if (data.type === 'flow_control') {
                // Manejar flow control (MORADO)
                console.log("🟣 [FRONTEND] FLOW_CONTROL recibido:", data);
                const msgId = botMessage.id;
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === msgId
                      ? {
                          ...msg,
                          flowControl: data.content,
                          flowControlType: 'flow_spec',
                        }
                      : msg,
                  ),
                );
                
              } else if (data.type === 'thinking') {
                console.log("🤔 [FRONTEND] Mensaje de pensando recibido:", data.message);
                
                const msgId = botMessage.id;
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === msgId
                      ? {
                          ...msg,
                          text: data.message,
                          isStreaming: true,
                          isThinking: true,
                          trace: data.trace || [],
                        }
                      : msg,
                  ),
                );
                
              } else if (data.type === 'thinking_complete') {
                console.log("✅ [FRONTEND] Thinking completado:", data.message);
                
                const msgId = botMessage.id;
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === msgId
                      ? {
                          ...msg,
                          text: data.message,
                          isStreaming: true,
                          isThinking: true,
                          trace: data.trace || [],
                        }
                      : msg,
                  ),
                );
                
              } else if (data.type === 'sql_executing') {
                console.log("🔍 [FRONTEND] SQL ejecutándose:", data.message);
                
                const msgId = botMessage.id;
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === msgId
                      ? {
                          ...msg,
                          text: data.message,
                          isStreaming: true,
                          isThinking: true,
                          trace: data.trace || [],
                        }
                      : msg,
                  ),
                );
                
              } else if (data.type === 'sql_trace') {
                console.log("📋 [FRONTEND] SQL TRACE recibido:", data.trace);
                
                // Guardar el trace SQL en el mensaje
                const msgId = botMessage.id;
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === msgId
                      ? {
                          ...msg,
                          trace: data.trace || [],
                          sqlExecutionTime: data.executionTime,
                        }
                      : msg,
                  ),
                );
                
              } else if (data.type === 'end') {
                console.log("✅ [FRONTEND] Stream de regeneración finalizado exitosamente");
                
                // Limpiar el intervalo y mostrar cualquier contenido restante
                if (streamingInterval) {
                  clearInterval(streamingInterval);
                  flushBuffer(); // Mostrar lo que quede en el buffer
                }
                
                // Capturar variables en el scope para evitar problemas de closure
                const msgId = botMessage.id;
                const finalResponse = data.fullResponse || fullResponse;
                
                // Finalizar el streaming
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === msgId
                      ? {
                          ...msg,
                          text: finalResponse,
                          isStreaming: false,
                          isThinking: false,
                          trace: prev.find(m => m.id === msgId)?.trace?.map(step => 
                            step.id === "2" ? { ...step, status: "completed", endTime: new Date().toISOString(), duration: 2 } : step
                          ) || [],
                        }
                      : msg,
                  ),
                );
                break;
              } else if (data.type === 'error') {
                console.error("❌ [FRONTEND] Error en stream:", data.message);
                throw new Error(data.message || "Error en el streaming");
              }
            } catch (parseError) {
              console.warn("⚠️ [FRONTEND] Error parseando línea:", line, parseError);
              // Continuar con el siguiente chunk
            }
          }
        }
      } finally {
        // Limpiar recursos
        if (streamingInterval) {
          clearInterval(streamingInterval);
        }
        reader.releaseLock();
      }
      
    } catch (error) {
      console.error("❌ Error al regenerar:", error);
      const msgId = botMessage.id;
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === msgId
            ? {
                ...msg,
                text: "Hubo un error al regenerar la respuesta. Por favor, intenta de nuevo.",
                isStreaming: false,
                isError: true,
              }
            : msg
        )
      );
    } finally {
      setIsTyping(false);
    }
  }; */

  /* const handleReadAloud = async (messageText) => {
    // CANCELAR TODO: peticiones HTTP pendientes Y audio actual
    if (isPlayingAudioRef.current || audioAbortControllerRef.current) {
      console.log('🛑 CANCELANDO TODO');
      
      if (audioAbortControllerRef.current) {
        audioAbortControllerRef.current.abort();
        audioAbortControllerRef.current = null;
      }
      
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      
      isPlayingAudioRef.current = false;
      setIsPlayingAudio(false);
      
      return;
    }
    
    console.log('🔊 TTS - voz nova');
    
    // BLOQUEAR INMEDIATAMENTE
    isPlayingAudioRef.current = true;
    setIsPlayingAudio(true);
    
    // IMPORTANTE: Crear Audio ANTES del fetch (para iOS)
    const audio = new Audio();
    currentAudioRef.current = audio;
    
    const abortController = new AbortController();
    audioAbortControllerRef.current = abortController;
    
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("No autenticado");

      const t0 = Date.now();
      
      // Limitar texto
      let textToSend = messageText;
      if (textToSend.length > 800) {
        textToSend = textToSend.substring(0, 800) + '...';
        console.log('⚠️ Texto truncado a 800 caracteres');
      }
      
      // Fetch
      const response = await fetch(`${API_URL}/api/text-to-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          text: textToSend,
          voice: 'nova',
        }),
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Error servidor');

      console.log('⚡ Respuesta:', Date.now() - t0, 'ms');
      
      const blob = await response.blob();
      console.log('📦', blob.size, 'bytes, tipo:', blob.type);
      
      if (blob.size === 0) throw new Error('Vacío');
      
      audioAbortControllerRef.current = null;
      
      // Asignar la fuente al audio ya creado
      const url = URL.createObjectURL(blob);
      audio.src = url;
      audio.load(); // Forzar carga
      
      // Reproducir - esto ahora SÍ funciona en iOS porque el Audio se creó en el click
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('🎵 Play:', Date.now() - t0, 'ms');
          })
          .catch((e) => {
            console.error('❌ Play error:', e);
            isPlayingAudioRef.current = false;
            setIsPlayingAudio(false);
          });
      }
      
      audio.onended = () => {
        URL.revokeObjectURL(url);
        currentAudioRef.current = null;
        isPlayingAudioRef.current = false;
        setIsPlayingAudio(false);
        console.log('✅ Terminado');
      };
      
      audio.onerror = (e) => {
        console.error('❌ Audio error:', e);
        URL.revokeObjectURL(url);
        currentAudioRef.current = null;
        isPlayingAudioRef.current = false;
        setIsPlayingAudio(false);
      };
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🛑 Abortado');
        currentAudioRef.current = null;
        isPlayingAudioRef.current = false;
        setIsPlayingAudio(false);
        return;
      }
      
      console.error('❌', error.message);
      currentAudioRef.current = null;
      audioAbortControllerRef.current = null;
      isPlayingAudioRef.current = false;
      setIsPlayingAudio(false);
    }
  }; */

  /* const handleCopyMessage = (messageText) => {
    navigator.clipboard.writeText(messageText).then(() => {
      console.log('📋 Mensaje copiado al portapapeles');
      // Puedes agregar una notificación visual aquí
    }).catch(err => {
      console.error('❌ Error al copiar:', err);
    });
  };

  const handleShareMessage = (messageText) => {
    console.log('🔗 Compartiendo mensaje');
    if (navigator.share) {
      navigator.share({
        title: 'Mensaje de Deitana',
        text: messageText
      });
    } else {
      // Fallback: copiar al portapapeles
      handleCopyMessage(messageText);
    }
  };

  const handleLikeMessage = async (messageId) => {
    console.log('👍 Like en mensaje:', messageId);
    await sendFeedback(messageId, 'like');
  };

  const handleDislikeMessage = async (messageId) => {
    console.log('👎 Dislike en mensaje:', messageId);
    await sendFeedback(messageId, 'dislike');
  }; */

  // Estado para tracking de feedback por mensaje
  // const [messageFeedback, setMessageFeedback] = useState({}); // { messageId: 'like' | 'dislike' | null }

  // 🧠 NUEVA FUNCIÓN: Enviar feedback al sistema de aprendizaje (comentada - no utilizada)
  /* const sendFeedback = async (messageId, feedback) => {
    try {
      // 1. ⚡ FEEDBACK VISUAL INSTANTÁNEO
      const currentFeedback = messageFeedback[messageId];
      
      // Si es el mismo feedback, toggle (deshacer)
      if (currentFeedback === feedback) {
        setMessageFeedback(prev => ({ ...prev, [messageId]: null }));
        console.log(`🔄 [FEEDBACK] Deshaciendo ${feedback} para mensaje ${messageId}`);
        
        // Enviar "remove" al backend asíncronamente
        sendFeedbackToBackend(messageId, 'remove', currentFeedback);
        return;
      }
      
      // Actualizar estado inmediatamente
      setMessageFeedback(prev => ({ ...prev, [messageId]: feedback }));
      console.log(`⚡ [FEEDBACK] ${feedback.toUpperCase()} aplicado instantáneamente`);
      
      // 2. 🌐 ENVIAR AL BACKEND ASÍNCRONAMENTE (sin esperar)
      sendFeedbackToBackend(messageId, feedback, currentFeedback);
      
    } catch (error) {
      console.error('❌ [FEEDBACK] Error en sendFeedback:', error);
    }
  };

  // Función asíncrona para enviar al backend
  const sendFeedbackToBackend = async (messageId, feedback, previousFeedback = null) => {
    try {
      // Encontrar el mensaje
      const message = chatMessages.find(msg => msg.id === messageId);
      if (!message || message.sender !== 'bot') {
        console.log('⚠️ [FEEDBACK] Mensaje no encontrado o no es del bot');
        return;
      }

      // Encontrar el mensaje del usuario que generó esta respuesta
      const messageIndex = chatMessages.findIndex(msg => msg.id === messageId);
      const userMessage = messageIndex > 0 ? chatMessages[messageIndex - 1] : null;
      
      if (!userMessage || userMessage.sender !== 'user') {
        console.log('⚠️ [FEEDBACK] No se encontró el mensaje del usuario correspondiente');
        return;
      }

      // Extraer información del SQL trace si existe
      let sqlQuery = '';
      let hadErrors = false;
      let detectedScope = 'semillero'; // Por defecto
      
      // 1. Detectar scope desde el QUERY del usuario primero (más confiable)
      const userQueryLower = userMessage.text.toLowerCase();
      const hasHuertoKeywords = ['huerto', 'garden', 'tienda', 'minorista', 'plantasdehuerto'].some(kw => userQueryLower.includes(kw));
      const hasSemilleroKeywords = ['semillero', 'producción', 'invernadero', 'partida', 'injerto'].some(kw => userQueryLower.includes(kw));
      const hasEcoKeywords = ['eco', 'ecológico', 'ecodeitana', 'sostenible', 'certificación'].some(kw => userQueryLower.includes(kw));
      
      // Contar cuántos scopes están activos
      const activeScopes = [hasHuertoKeywords, hasSemilleroKeywords, hasEcoKeywords].filter(Boolean).length;
      
      if (activeScopes > 1) {
        detectedScope = 'multiple';
      } else if (hasEcoKeywords) {
        detectedScope = 'eco';
      } else if (hasHuertoKeywords) {
        detectedScope = 'huerto';
      } else if (hasSemilleroKeywords) {
        detectedScope = 'semillero';
      }
      
      // 2. Extraer SQL del trace
      if (message.trace && message.trace.length > 0) {
        // Buscar todas las consultas SQL en el trace
        const allQueries = [];
        message.trace.forEach(traceItem => {
          if (traceItem.query) {
            allQueries.push(traceItem.query);
            // Detectar errores
            if (traceItem.status === 'error' || traceItem.status === 'failed') {
              hadErrors = true;
            }
          }
        });
        
        // Combinar todas las consultas SQL
        sqlQuery = allQueries.join('; ');
        
        // 3. Refinar scope basado en el SQL (sobrescribe si es más específico)
        const hasHuertoSQL = sqlQuery.includes('huerto.');
        const hasSemilleroSQL = sqlQuery.includes('eja.');
        const hasEcoSQL = sqlQuery.includes('ecos.');
        const activeSQLScopes = [hasHuertoSQL, hasSemilleroSQL, hasEcoSQL].filter(Boolean).length;
        
        if (activeSQLScopes > 1) {
          detectedScope = 'multiple';
        } else if (hasEcoSQL) {
          detectedScope = 'eco';
        } else if (hasHuertoSQL) {
          detectedScope = 'huerto';
        } else if (hasSemilleroSQL) {
          detectedScope = 'semillero';
        }
      }

      // Preparar datos para enviar
      const feedbackData = {
        query: userMessage.text,
        sql: sqlQuery,
        response: message.text,
        feedback: feedback,
        scope: detectedScope,
        hadErrors: hadErrors,
        conversationId: currentConversationId,
        previousFeedback: previousFeedback, // Para manejar cambios
        messageId: messageId
      };

      console.log('🌐 [FEEDBACK] Enviando al backend:', {
        feedback: feedback,
        scope: detectedScope,
        query: userMessage.text.substring(0, 50) + '...',
        sqlLength: sqlQuery.length,
        sqlPreview: sqlQuery.substring(0, 100) + (sqlQuery.length > 100 ? '...' : ''),
        hadErrors: hadErrors,
        isToggle: feedback === 'remove',
        traceItems: message.trace ? message.trace.length : 0
      });

      // Enviar al backend
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(feedbackData)
      });

      const result = await response.json();
      
      if (result.success) {
        console.log(`✅ [FEEDBACK] ${feedback.toUpperCase()} procesado en backend`);
      } else {
        console.error('❌ [FEEDBACK] Error del servidor:', result.error);
        // Revertir estado visual en caso de error
        if (feedback !== 'remove') {
          setMessageFeedback(prev => ({ ...prev, [messageId]: previousFeedback }));
        }
      }

    } catch (error) {
      console.error('❌ [FEEDBACK] Error enviando al backend:', error);
      // Revertir estado visual en caso de error
      if (feedback !== 'remove') {
        setMessageFeedback(prev => ({ ...prev, [messageId]: previousFeedback }));
      }
    }
  }; */

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      // Si ya hay texto transcrito, preguntar si quiere agregar o reemplazar
      if (message.trim()) {
        const shouldAppend = window.confirm(
          'Ya tienes texto transcrito. ¿Quieres agregar la nueva grabación al texto existente?\n\n' +
          '• Aceptar: Agregar al texto existente\n' +
          '• Cancelar: Reemplazar el texto existente'
        );
        
        if (shouldAppend) {
          // Agregar espacio antes de la nueva grabación
          setMessage(prev => prev + ' ');
        } else {
          // Limpiar el texto existente
          setMessage('');
        }
      }
      startRecording();
    }
  };

  const handleSquareClick = () => {
    console.log('⏹️ Deteniendo grabación...');
    if (isRecording) {
      stopRecording();
    } else if (isTranscribing) {
      // Si está transcribiendo, cancelar la transcripción
      setIsTranscribing(false);
      setIsTranscriptionPaused(true);
    }
  };

  const handleArrowUpClick = () => {
    if (message.trim() || selectedImage) {
      console.log('⬆️ Enviando mensaje transcrito...');
      // Enviar el mensaje actual
      handleSubmit({ preventDefault: () => {} });
    }
  };

  // Manejar Shift+Enter para nueva línea y Enter para enviar
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // En móviles, Enter siempre hace salto de línea (no envía)
      if (isMobile) {
        // Dejar el comportamiento por defecto (salto de línea)
        return;
      }
      
      // En desktop: Enter envía, Shift+Enter nueva línea
      if (!e.shiftKey) {
        e.preventDefault();
        // No enviar si la IA está escribiendo
        if (!isTyping) {
          handleSubmit(e);
        }
      }
    }
  };

  // Auto-expandir el textarea
  const handleTextareaChange = (e) => {
    setMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  // Resetear altura cuando el mensaje esté vacío
  useEffect(() => {
    if (message === '') {
      const textarea = document.querySelector('.ds-chat-input');
      if (textarea) {
        textarea.style.height = 'auto';
      }
    }
  }, [message]);

  // Función para detener el streaming
  const stopStreaming = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🛑 Deteniendo streaming...');
    
    // Establecer flag para detener el loop
    shouldStopStreamingRef.current = true;
    
    // Cancelar el reader si existe
    if (streamReaderRef.current) {
      try {
        streamReaderRef.current.cancel();
      } catch (err) {
        console.log('Reader ya estaba cancelado');
      }
      streamReaderRef.current = null;
    }
    
    // Limpiar el intervalo si existe
    if (streamIntervalRef.current) {
      clearInterval(streamIntervalRef.current);
      streamIntervalRef.current = null;
    }
    
    // Actualizar el estado para indicar que ya no está escribiendo
    setIsTyping(false);
    
    // Finalizar el último mensaje del bot sin agregar texto adicional
    setChatMessages(prev => 
      prev.map(msg => 
        msg.sender === 'bot' && msg.isStreaming
          ? { ...msg, isStreaming: false }
          : msg
      )
    );
  };

  // Función para manejar la selección de imágenes para el chat
  const handleImageSelectForChat = () => {
    const fileInput = document.getElementById('chat-image-input')
    fileInput?.click()
  }

  const handleImageUploadForChat = (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      alert("Por favor selecciona un archivo de imagen válido")
      return
    }

    // Validar tamaño (máximo 10MB para chat)
    if (file.size > 10 * 1024 * 1024) {
      alert("La imagen debe ser menor a 10MB")
      return
    }

    setSelectedImage(file)
    console.log("Imagen seleccionada para el chat:", file.name)
  }

  const removeSelectedImage = () => {
    setSelectedImage(null)
    // Limpiar el input file
    const fileInput = document.getElementById('chat-image-input')
    if (fileInput) {
      fileInput.value = ''
    }
  }

  // NOTA: Todas las funciones de voz ahora están en VoiceAssistantModal.jsx

  // Función para convertir archivo a base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })
  }

  // Modificar la función handleSubmit para usar la conversación actual
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!message.trim() && !selectedImage) return
    
    // Prevenir enviar mensajes mientras la IA está respondiendo
    if (isTyping) {
      console.log("⚠️ [FRONTEND] La IA está respondiendo. Espera a que termine o detén la respuesta.");
      return;
    }

    console.log("🚀 [FRONTEND] === INICIO ENVÍO DE MENSAJE CON STREAMING ===")
    console.log("🚀 [FRONTEND] Mensaje a enviar:", message)
    console.log("🚀 [FRONTEND] Conversation ID actual:", currentConversationId)

    // Resetear flag de detención
    shouldStopStreamingRef.current = false;

    const userMessage = {
      id: Date.now(),
      text: message || (selectedImage ? `📷 Imagen: ${selectedImage.name}` : ""),
      sender: "user",
      image: selectedImage ? {
        file: selectedImage,
        name: selectedImage.name,
        size: selectedImage.size,
        type: selectedImage.type
      } : null,
      // Guardar referencia a la imagen para mostrar en el chat
      hasImage: !!selectedImage
    }

    setChatMessages((prev) => [...prev, userMessage])
    setMessage("")
    setIsTyping(true)
    
    // Si hay imagen, mostrar mensaje especial
    if (selectedImage) {
      console.log("🖼️ [FRONTEND] Procesando imagen:", selectedImage.name);
    }

    // Crear mensaje del bot con estado de streaming
    const botMessage = {
      id: Date.now() + 1,
      text: selectedImage ? "🖼️ Analizando imagen y generando consulta..." : "",
      sender: "bot",
      isStreaming: true,
    }

    setChatMessages((prev) => {
      console.log("🤖 [FRONTEND] Creando nuevo mensaje del bot:", botMessage)
      return [...prev, botMessage]
    })

    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        throw new Error("No hay usuario autenticado")
      }

      console.log("🔑 [FRONTEND] Token obtenido, iniciando streaming...")
      console.log("🖼️ [FRONTEND] Imagen seleccionada:", selectedImage ? selectedImage.name : "Ninguna")

      // =====================================
      // NUEVA IMPLEMENTACIÓN CON STREAMING NATURAL
      // =====================================
      
      const response = await fetch(`${API_URL}/api/chat/stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        body: JSON.stringify({ 
          message,
          conversationId: currentConversationId,
          image: selectedImage ? await (async () => {
            console.log("🔄 [FRONTEND] Convirtiendo imagen a base64...");
            const base64 = await convertFileToBase64(selectedImage);
            console.log("✅ [FRONTEND] Imagen convertida, tamaño base64:", base64.length);
            return base64;
          })() : null
        }),
        })

        if (!response.ok) {
        throw new Error(`Error del servidor: ${response.status}`)
      }

      console.log("✅ [FRONTEND] Conexión de streaming establecida")

      // Variables para streaming natural
      const reader = response.body.getReader()
      streamReaderRef.current = reader // Guardar en ref para poder cancelar
      const decoder = new TextDecoder()
      let fullResponse = ""
      let buffer = ""
      let streamingInterval = null

      // Función para mostrar el buffer acumulado de forma suave
      const flushBuffer = () => {
        if (buffer.trim()) {
          fullResponse += buffer
          
          // Capturar variables para evitar problemas de closure
          const messageId = botMessage.id
          const currentResponse = fullResponse
          
          setChatMessages((prev) =>
            prev.map((msg) =>
                msg.id === messageId
              ? {
                  ...msg,
                    text: currentResponse,
                    isStreaming: true,
                }
              : msg,
            ),
          )

          console.log("📝 [FRONTEND] Mostrando buffer:", buffer.trim())
          buffer = ""
        }
      }

      // Intervalo para mostrar contenido de forma muy fluida (cada 30ms)
      streamingInterval = setInterval(() => {
        flushBuffer() // Mostrar cualquier contenido pendiente cada 30ms
      }, 30)
      streamIntervalRef.current = streamingInterval // Guardar en ref para poder cancelar

      try {
        while (true) {
          // Verificar si se debe detener el streaming
          if (shouldStopStreamingRef.current) {
            console.log("🛑 [FRONTEND] Streaming detenido por el usuario");
            break;
          }
          
          const { done, value } = await reader.read()
          
          if (done) {
            console.log("🏁 [FRONTEND] Stream completado")
            break
          }

          // Decodificar el chunk
          const chunk = decoder.decode(value, { stream: true })
          const lines = chunk.split('\n').filter(line => line.trim())

                      for (const line of lines) {
              try {
                // Quitar el prefijo "data: " si existe
                const jsonString = line.startsWith('data: ') ? line.slice(6) : line;
                const data = JSON.parse(jsonString)
                
                if (data.type === 'chunk' && data.content) {
                // Filtrar contenido válido y agregarlo al buffer
                const content = data.content
                
                // Solo agregar contenido que no sea vacío o caracteres extraños
                if (content && content.trim()) {
                  buffer += content
                } else if (content === ' ' || content === '\n') {
                  // Preservar espacios y saltos de línea
                  buffer += content
                }
                
              } else if (data.type === 'status_update') {
                // Manejar actualizaciones de estado (ROJO)
                console.log("🔴 [FRONTEND] STATUS_UPDATE recibido:", data)
                console.log("🔍 [FRONTEND] Scope recibido del backend:", data.scope)
                const messageId = botMessage.id
                const finalScope = data.scope || 'semillero'
                console.log("✅ [FRONTEND] Scope final que se guardará:", finalScope)
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          statusUpdate: data.content,
                          statusUpdateType: 'status_update_spec',
                          isStreaming: true,
                          // Capturar scope REAL del backend (huerto/semillero/eco/multiple)
                          detectedScope: finalScope,
                        }
                      : msg,
                  ),
                )
                
              } else if (data.type === 'summary') {
                // Manejar resúmenes (VERDE)
                console.log("🟢 [FRONTEND] SUMMARY recibido:", data)
                const messageId = botMessage.id
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          summary: data.content,
                          summaryType: 'summary_spec',
                        }
                      : msg,
                  ),
                )
                
              } else if (data.type === 'completion') {
                // Manejar completion tracking (AZUL)
                console.log("🔵 [FRONTEND] COMPLETION recibido:", data)
                const messageId = botMessage.id
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          completion: data.content,
                          completionType: 'completion_spec',
                        }
                      : msg,
                  ),
                )
                
              } else if (data.type === 'flow_control') {
                // Manejar flow control (MORADO)
                console.log("🟣 [FRONTEND] FLOW_CONTROL recibido:", data)
                const messageId = botMessage.id
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          flowControl: data.content,
                          flowControlType: 'flow_spec',
                        }
                      : msg,
                  ),
                )
                
              } else if (data.type === 'thinking') {
                console.log("🤔 [FRONTEND] Mensaje de pensando recibido:", data.message)
                
                // Mostrar mensaje de "pensando" al usuario con estilos especiales
                const messageId = botMessage.id
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          text: data.message,
                          isStreaming: true,
                          isThinking: true, // Marcar como mensaje de thinking
                          trace: data.trace || [], // Agregar trace data
                        }
                      : msg,
                  ),
                )
                
              } else if (data.type === 'thinking_complete') {
                console.log("✅ [FRONTEND] Thinking completado:", data.message)
                
                // Actualizar el mensaje con el trace completo
                const messageId = botMessage.id
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          text: data.message,
                          isStreaming: true,
                          isThinking: true,
                          trace: data.trace || [],
                        }
                      : msg,
                  ),
                )
                
              } else if (data.type === 'sql_executing') {
                console.log("🔍 [FRONTEND] SQL ejecutándose:", data.message)
                
                // Actualizar el mensaje con el trace de SQL
                const messageId = botMessage.id
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          text: data.message,
                          isStreaming: true,
                          isThinking: true,
                          trace: data.trace || [],
                        }
                      : msg,
                  ),
                )
                
              } else if (data.type === 'sql_trace') {
                console.log("📋 [FRONTEND] SQL TRACE recibido:", data.trace);
                
                // Guardar el trace SQL en el mensaje
                const messageId = botMessage.id;
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          trace: data.trace || [],
                          sqlExecutionTime: data.executionTime,
                        }
                      : msg,
                  ),
                );
                
              } else if (data.type === 'end') {
                console.log("✅ [FRONTEND] Stream finalizado exitosamente")
                
                // Actualizar conversationId si viene del servidor
                if (data.conversationId && (!currentConversationId || currentConversationId.startsWith('temp_'))) {
                  console.log("🔄 [FRONTEND] Actualizando conversationId:", data.conversationId)
                  setCurrentConversationId(data.conversationId)
                  // Actualizar la URL para reflejar la nueva conversación
                  setSearchParams({ chat: data.conversationId })
                }
                
                // Limpiar el intervalo y mostrar cualquier contenido restante
                if (streamingInterval) {
                  clearInterval(streamingInterval)
                  flushBuffer() // Mostrar lo que quede en el buffer
                }
                
                // Capturar variables en el scope para evitar problemas de closure
                const messageId = botMessage.id
                const finalResponse = data.fullResponse || fullResponse
                
                // Finalizar el streaming
        setChatMessages((prev) =>
          prev.map((msg) =>
                    msg.id === messageId
              ? {
                  ...msg,
                          text: finalResponse,
                          isStreaming: false, // Finalizar streaming
                          isThinking: false, // Limpiar el estado de thinking
                          trace: prev.find(m => m.id === messageId)?.trace?.map(step => 
                            step.id === "2" ? { ...step, status: "completed", endTime: new Date().toISOString(), duration: 2 } : step
                          ) || [], // Mantener trace y marcar SQL como completado
                }
              : msg,
          ),
        )
                break
              } else if (data.type === 'error') {
                console.error("❌ [FRONTEND] Error en stream:", data.message)
                throw new Error(data.message || "Error en el streaming")
              }
            } catch (parseError) {
              console.warn("⚠️ [FRONTEND] Error parseando línea:", line, parseError)
              // Continuar con el siguiente chunk
            }
          }
        }
      } finally {
        // Limpiar recursos
        if (streamingInterval) {
          clearInterval(streamingInterval)
        }
        reader.releaseLock()
      }

    } catch (error) {
      console.error("❌ [FRONTEND] Error en streaming:", error)
      
      // Capturar variable para evitar problemas de closure
      const messageId = botMessage.id
      
      // Actualizar el mensaje del bot con el error
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                text: "Hubo un error al conectarse con el servidor. Por favor, intenta de nuevo.",
                isStreaming: false,
                isError: true,
              }
            : msg,
        ),
      )
    } finally {
      setIsTyping(false)
      setSelectedImage(null) // Limpiar la imagen seleccionada después del procesamiento
      streamReaderRef.current = null // Limpiar ref
      streamIntervalRef.current = null // Limpiar ref
      shouldStopStreamingRef.current = false // Resetear flag
      console.log("🏁 [FRONTEND] === FIN ENVÍO DE MENSAJE ===")
    }
  }

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [chatMessages, isTyping])

  useEffect(() => {
    const chatContainer = chatContainerRef.current
    if (!chatContainer) return

    const handleScroll = () => {
      const isScrolledUp = chatContainer.scrollTop < chatContainer.scrollHeight - chatContainer.clientHeight - 20
      setShowScrollButton(isScrolledUp && chatMessages.length > 0)
    }

    chatContainer.addEventListener("scroll", handleScroll)
    setTimeout(() => handleScroll(), 500)

    return () => chatContainer.removeEventListener("scroll", handleScroll)
  }, [chatMessages])

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  const getScrollButtonStyle = () => {
    if (!mainContentRef.current) return {}

    const mainContentRect = mainContentRef.current.getBoundingClientRect()
    const centerX = mainContentRect.left + mainContentRect.width / 2

    return {
      left: `${centerX}px`,
      transform: "translateX(-50%)",
    }
  }

  const isChatEmpty = chatMessages.length === 0 && !isTyping

  // Funciones para drag del bottom sheet iOS
  const handleTouchStart = (e) => {
    setIsDragging(true)
    setStartY(e.touches[0].clientY)
  }

  const handleTouchMove = (e) => {
    if (!isDragging) return

    const currentY = e.touches[0].clientY
    const deltaY = currentY - startY

    if (deltaY > 0) {
      setDragY(deltaY)
    }
  }

  const handleTouchEnd = () => {
    setIsDragging(false)

    if (dragY > 100) {
      setUserMenuOpen(false)
      // Resetear al menú principal cuando se cierra
      setTimeout(() => setModalView("main"), 300)
    }

    setDragY(0)
  }

  const handleMouseDown = (e) => {
    setIsDragging(true)
    setStartY(e.clientY)
  }

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return

    const currentY = e.clientY
    const deltaY = currentY - startY

    if (deltaY > 0) {
      setDragY(deltaY)
    }
  }, [isDragging, startY])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)

    if (dragY > 100) {
      setUserMenuOpen(false)
      // Resetear al menú principal cuando se cierra
      setTimeout(() => setModalView("main"), 300)
    }

    setDragY(0)
  }, [dragY])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)

      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // Funciones para drag del search bottom sheet
  const handleSearchTouchStart = (e) => {
    setIsSearchDragging(true)
    setSearchStartY(e.touches[0].clientY)
  }

  const handleSearchTouchMove = (e) => {
    if (!isSearchDragging) return

    const currentY = e.touches[0].clientY
    const deltaY = currentY - searchStartY

    if (deltaY > 0) {
      setSearchDragY(deltaY)
    }
  }

  const handleSearchTouchEnd = () => {
    setIsSearchDragging(false)

    if (searchDragY > 100) {
      setSearchModalOpen(false)
      setSearchQuery("")
      setDebouncedSearchQuery("")
    }

    setSearchDragY(0)
  }

  const handleSearchMouseDown = (e) => {
    setIsSearchDragging(true)
    setSearchStartY(e.clientY)
  }

  const handleSearchMouseMove = useCallback((e) => {
    if (!isSearchDragging) return

    const currentY = e.clientY
    const deltaY = currentY - searchStartY

    if (deltaY > 0) {
      setSearchDragY(deltaY)
    }
  }, [isSearchDragging, searchStartY])

  const handleSearchMouseUp = useCallback(() => {
    setIsSearchDragging(false)

    if (searchDragY > 100) {
      setSearchModalOpen(false)
      setSearchQuery("")
      setDebouncedSearchQuery("")
    }

    setSearchDragY(0)
  }, [searchDragY])

  useEffect(() => {
    if (isSearchDragging) {
      document.addEventListener("mousemove", handleSearchMouseMove)
      document.addEventListener("mouseup", handleSearchMouseUp)

      return () => {
        document.removeEventListener("mousemove", handleSearchMouseMove)
        document.removeEventListener("mouseup", handleSearchMouseUp)
      }
    }
  }, [isSearchDragging, handleSearchMouseMove, handleSearchMouseUp])

  // Añadir estilos CSS para el indicador de escritura en línea
  useEffect(() => {
    const style = document.createElement("style")
    style.innerHTML = `
    .ds-typing-indicator-inline {
      display: inline-flex;
      align-items: center;
      margin-left: 5px;
    }
    
    .ds-typing-indicator-inline span {
      height: 6px;
      width: 6px;
      background-color: #333;
      border-radius: 50%;
      display: inline-block;
      margin: 0 2px;
      opacity: 0.6;
      animation: ds-typing 1s infinite;
    }
    
    .ds-typing-indicator-inline span:nth-child(1) {
      animation-delay: 0s;
    }
    
    .ds-typing-indicator-inline span:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    .ds-typing-indicator-inline span:nth-child(3) {
      animation-delay: 0.4s;
    }
    
    @keyframes ds-typing {
      0% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-5px);
      }
      100% {
        transform: translateY(0px);
      }
    }

    .ds-message-content p {
      margin: 0;
      line-height: 1.6;
      color: #333;
      font-size: 15px;
      margin-bottom: 12px;
    }

    .ds-message-content strong {
      font-weight: 600;
      font-size: 15px;
      color: #333;
      display: inline;
      margin-right: 4px;
    }

    .ds-message-content em {
      font-style: italic;
      color: #333;
      font-size: 15px;
    }

    .ds-message-content ul, .ds-message-content ol {
      margin: 8px 0;
      padding-left: 20px;
      color: #333;
      font-size: 15px;
    }

    .ds-message-content li {
      margin: 5px 0;
      color: #333;
      font-size: 15px;
    }

    /* Permitir diferentes estilos de listas según el contexto */
    .ds-message-content ul li {
      list-style-type: disc;
    }

    .ds-message-content ol li {
      list-style-type: decimal;
    }

    /* Estilos para tablas */
    .ds-message-content table {
      border-collapse: collapse;
      width: 100%;
      margin: 12px 0;
    }

    .ds-message-content th, .ds-message-content td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }

    .ds-message-content th {
      background-color: #f5f5f5;
      font-weight: 600;
    }

    .ds-message-content a {
      color: #2964aa;
      text-decoration: none;
      font-weight: 500;
      border-bottom: 1px solid #2964aa;
      padding-bottom: 1px;
      transition: all 0.2s ease;
    }

    .ds-message-content a:hover {
      color: #1a4b8c;
      border-bottom-color: #1a4b8c;
    }
  `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    const diffInHours = Math.floor(diffInMinutes / 60)
    const diffInDays = Math.floor(diffInHours / 24)
    const diffInWeeks = Math.floor(diffInDays / 7)
    const diffInMonths = Math.floor(diffInDays / 30)

    if (diffInMonths > 0) {
      return `Hace ${diffInMonths} ${diffInMonths === 1 ? "mes" : "meses"}`
    } else if (diffInWeeks > 0) {
      return `Hace ${diffInWeeks} ${diffInWeeks === 1 ? "semana" : "semanas"}`
    } else if (diffInDays > 0) {
      return `Hace ${diffInDays} ${diffInDays === 1 ? "día" : "días"}`
    } else if (diffInHours > 0) {
      return `Hace ${diffInHours} ${diffInHours === 1 ? "hora" : "horas"}`
    } else if (diffInMinutes > 0) {
      return `Hace ${diffInMinutes} ${diffInMinutes === 1 ? "minuto" : "minutos"}`
    } else {
      return "Hace un momento"
    }
  }

  // Función para filtrar chats por término de búsqueda
  const filterChatsBySearch = (chats, query) => {
    if (!query.trim()) return chats

    const searchTerm = query.toLowerCase().trim()

    return chats.filter((chat) => {
      const title = chat.title === "NUEVA_CONEXION" ? "Nueva conversación" : chat.title
      const titleLower = title.toLowerCase()

      // Búsqueda simple: si el título contiene el término de búsqueda
      return titleLower.includes(searchTerm)
    })
  }

  // En la función formatTimeAgo, agregar una nueva función para agrupar por fechas
  const groupChatsByDate = (chats) => {
    const groups = {}
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    chats.forEach((chat) => {
      // Validar que exista una fecha válida
      const dateValue = chat.updatedAt || chat.created_at || chat.timestamp
      if (!dateValue) {
        // Si no hay fecha, agrupar en "Sin fecha"
        const groupKey = "Sin fecha"
        if (!groups[groupKey]) {
          groups[groupKey] = []
        }
        groups[groupKey].push(chat)
        return
      }

      // Intentar crear la fecha
      const chatDate = new Date(dateValue)
      
      // Validar que la fecha sea válida
      if (isNaN(chatDate.getTime())) {
        // Si la fecha es inválida, agrupar en "Sin fecha"
        const groupKey = "Sin fecha"
        if (!groups[groupKey]) {
          groups[groupKey] = []
        }
        groups[groupKey].push(chat)
        return
      }

      const chatDateOnly = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate())
      const diffInDays = Math.floor((today - chatDateOnly) / (1000 * 60 * 60 * 24))

      let groupKey
      if (diffInDays === 0) {
        groupKey = "Hoy"
      } else if (diffInDays === 1) {
        groupKey = "Ayer"
      } else if (diffInDays <= 7) {
        groupKey = "Hace una semana"
      } else if (diffInDays <= 30) {
        groupKey = "Hace un mes"
      } else {
        // Para fechas más antiguas, mostrar la fecha absoluta
        const options = { day: "numeric", month: "long" }
        groupKey = chatDate.toLocaleDateString("es-ES", options)
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(chat)
    })

    return groups
  }

  // Actualizar las fechas cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeUpdate((prev) => prev + 1)
    }, 60000) // 60000 ms = 1 minuto

    return () => clearInterval(interval)
  }, [])

  // Debounce para la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 150) // 150ms de delay

    return () => clearTimeout(timer)
  }, [searchQuery])

  const handleDeleteChat = async (conversationId, e) => {
    e.stopPropagation()
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Error al eliminar la conversación")
      }

      // Actualiza el historial localmente
      setChatHistory((prev) => prev.filter((chat) => chat.id !== conversationId))

      // Si el chat eliminado es el actual, limpia la vista
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null)
        setChatMessages([])
        setSearchParams({})
      }
    } catch (error) {
      console.error("Error al eliminar la conversación:", error)
      // Puedes mostrar un mensaje de error al usuario si lo deseas
    }
  }

  // Función para mostrar más elementos del historial
  const handleShowMoreHistory = () => {
    setHistorialItemsToShow(prev => prev + HISTORIAL_INCREMENT)
  }

  // Función para obtener chats limitados por paginación
  const getLimitedChatHistory = () => {
    const groupedChats = groupChatsByDate(chatHistory)
    const limitedGroups = {}
    let totalShown = 0

    for (const [dateGroup, chats] of Object.entries(groupedChats)) {
      if (totalShown >= historialItemsToShow) break
      
      const remainingSlots = historialItemsToShow - totalShown
      const chatsToShow = chats.slice(0, remainingSlots)
      
      if (chatsToShow.length > 0) {
        limitedGroups[dateGroup] = chatsToShow
        totalShown += chatsToShow.length
      }
    }

    return {
      groups: limitedGroups,
      hasMore: totalShown < chatHistory.length
    }
  }

  return (
    <div className="ds-home-container">
      <style>
        {`
          @keyframes gradient-slide {
  0% {
    background-position: 400% 0;
  }
  100% {
    background-position: -400% 0;
  }
}

@keyframes gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

.thinking-message {
  background: linear-gradient(90deg, #666666, #cccccc, #999999, #666666);
  background-size: 400% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: gradient-slide 6s ease-in-out infinite;

  font-size: 15px;
  letter-spacing: 0.5px;
  white-space: pre-wrap !important;
  word-break: break-all !important;
  overflow-wrap: break-word !important;
  line-height: 1.6;
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border-left: 3px solid #cccccc;
  margin: 8px 0;
  display: block;
  width: 100%;
}

.thinking-message p {
  white-space: pre-wrap !important;
  word-break: break-all !important;
  overflow-wrap: break-word !important;
  margin: 0 !important;
}

.ai-thinking-text {
  background: linear-gradient(90deg, #666666, #cccccc, #999999, #666666);
  background-size: 400% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: gradient-slide 6s ease-in-out infinite;
  font-weight: normal;
  display: inline-block;
}

/* Estilos para la barra de iconos de interacción */
.message-actions-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.message-actions-bar:hover {
  opacity: 1;
}

.message-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: transparent;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  color: #666;
  transition: all 0.3s ease;
  min-width: 32px;
  height: 32px;
}

.message-action-btn:hover {
  background-color: #eeeced;
  color: black;
}

.message-action-btn:active {
  background-color: #e5e7eb;
  color: #111827;
}

/* Responsive: ocultar algunos iconos en móviles */
@media (max-width: 768px) {
  .message-actions-bar {
    gap: 2px;
  }
  
  .message-action-btn {
    padding: 4px;
    min-width: 28px;
    height: 28px;
  }
}

/* Estilos para el botón de Stop - igual que los iconos de mensaje */
.ds-stop-button {
  background-color: transparent !important;
  color: #666 !important;
  transition: all 0.2s ease;
}

.ds-stop-button:hover {
  background-color: #eeeced !important;
  color: black !important;
  transform: scale(1.05);
}

.ds-stop-button:active {
  background-color: #e5e7eb !important;
  color: #111827 !important;
  transform: scale(0.95);
}

}
        `}
      </style>

      {/* Sidebar */}
      {isMobile && mobileSidebarOpen && (
        <div className="ds-mobile-overlay" onClick={() => setMobileSidebarOpen(false)}></div>
      )}

      <div
        className={`ds-sidebar ${
          isMobile
            ? mobileSidebarOpen
              ? "ds-sidebar-mobile-open"
              : "ds-sidebar-mobile-closed"
            : sidebarOpen
              ? "ds-sidebar-expanded"
              : "ds-sidebar-collapsed"
        }`}
      >
        {sidebarOpen || mobileSidebarOpen ? (
          <>
            <div className="ds-sidebar-header">
              <div className="ds-logo-sidebar-container">
                <img src="/logosinnombresinfondo.png" alt="Logo" className="ds-collapsed-logo-img" />
              </div>
              <button className="ds-header-toggle-btn" onClick={toggleSidebar}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M4 12v-6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v8" />
                  <path d="M20 18h-17" />
                  <path d="M6 15l-3 3l3 3" />
                </svg>
              </button>
              {isMobile && (
                <button className="ds-mobile-close-button" onClick={() => setMobileSidebarOpen(false)}>
                  ×
                </button>
              )}
            </div>

                          <div className="ds-sidebar-content">
                {/* Navegación principal */}
                <div className="ds-navigation-menu">
                <button className="ds-nav-item" onClick={() => setSearchModalOpen(true)}>
                    <Search size={16} />
                    <span>Buscar</span>
                  </button>

                <button className="ds-nav-item" onClick={() => {
                  handleNewChat()
                  if (isMobile) {
                    setMobileSidebarOpen(false)
                  }
                }}>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z"
                    />
                  </svg>
                  <span>Chat</span>
                </button>

                <button
                  className={`ds-nav-item${proyectosExpanded ? " active" : ""}`}
                  onClick={() => {
                    if (activeSection !== "proyectos") {
                      setActiveSection("proyectos")
                      setProyectosExpanded(true)
                    } else {
                      setProyectosExpanded(!proyectosExpanded)
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>Proyectos</span>
                  <ChevronDown
                    size={16}
                    className={`ds-chevron ${proyectosExpanded ? "expanded" : ""}`}
                    style={{ marginLeft: "auto", transition: "transform 0.2s" }}
                  />
                </button>

                {/* Contenido de Proyectos */}
                {proyectosExpanded && (
                  <div className="ds-proyectos-content">
                    <button className="ds-nav-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        <line x1="12" y1="11" x2="12" y2="17" />
                        <line x1="9" y1="14" x2="15" y2="14" />
                      </svg>
                      <span>Nuevo proyecto</span>
                    </button>
                    <button className="ds-nav-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v12M9 9h6M9 15h6" stroke="#10b981" />
                      </svg>
                      <span>Inversiones</span>
                    </button>
                  </div>
                )}

                <button
                  className="ds-nav-item active"
                  onClick={() => {
                    navigate("/artefactos")
                    if (isMobile) {
                      setMobileSidebarOpen(false)
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                  <span>Artefactos</span>
                </button>

                <button
                  className={`ds-nav-item${historialExpanded ? " active" : ""}`}
                  onClick={() => {
                    if (activeSection !== "historial") {
                      setActiveSection("historial")
                      setHistorialExpanded(true)
                      setHistorialItemsToShow(10) // Reiniciar paginación
                    } else {
                      setHistorialExpanded(!historialExpanded)
                      if (!historialExpanded) {
                        setHistorialItemsToShow(10) // Reiniciar paginación al abrir
                      }
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                    <path d="M12 7v5l4 2" />
                  </svg>
                  <span>Historial</span>
                </button>
              </div>

              {/* Contenido dinámico basado en la sección activa */}

              {activeSection === "historial" && (
                <div className="ds-section-content">
                  {historialExpanded && (
                    <div className="ds-historial-content">
                      {(() => {
                        const { groups, hasMore } = getLimitedChatHistory()
                        return (
                          <>
                            {Object.entries(groups).map(([dateGroup, chats]) => (
                              <div key={dateGroup} className="ds-date-group">
                                <div className="ds-date-header">{dateGroup}</div>
                                {chats.map((chat) => (
                                  <button
                                    key={`${chat.id}-${timeUpdate}`}
                                    onClick={() => handleConversationClick(chat.id)}
                                    className={`ds-historial-item ${currentConversationId === chat.id ? "active" : ""}`}
                                  >
                                    <span className="ds-historial-title">
                                      {chat.title === "NUEVA_CONEXION" ? "Nueva conversación" : chat.title}
                                    </span>
                                    <button
                                      className="ds-historial-delete-button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteChat(chat.id, e)
                                      }}
                                      title="Eliminar chat"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </button>
                                ))}
                              </div>
                            ))}
                            
                            {hasMore && (
                              <div className="ds-show-more-container">
                                <button 
                                  className="ds-show-more-button"
                                  onClick={handleShowMoreHistory}
                                >
                                  Ver más ({chatHistory.length - historialItemsToShow} restantes)
                                </button>
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  )}
                </div>
              )}

              {activeSection === "chat" && (
                <div className="ds-section-content">
                  {/* The "Nuevo chat" button is removed, so this section is now empty */}
                </div>
              )}
            </div>

            {/* Footer con usuario */}
            <div className="ds-sidebar-footer">
              <div className="ds-user-section">
                <div className="ds-user-info">
                  <button className="ds-user-circle" onClick={() => {
                    setUserMenuOpen(!userMenuOpen)
                    if (isMobile) {
                      setMobileSidebarOpen(false)
                    }
                  }}>
                    {getUserAvatar()}
                  </button>
                  <div className="ds-user-details">
                    <div className="ds-user-name">{user?.displayName || "Usuario"}</div>
                    <div className="ds-user-email">{user?.email || ""}</div>
                  </div>
                </div>
              </div>
              {/* Modal del usuario */}
            </div>
          </>
        ) : (
          <div className="ds-sidebar-collapsed-content">
            {/* Header colapsado */}
            <div className="ds-sidebar-header">
              <div className="ds-logo-hover-container" onClick={toggleSidebar}>
                <img src="/logosinnombresinfondo.png" alt="Logo" className="ds-collapsed-logo-img ds-logo-default" />
                <svg
                  className="ds-logo-hover-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M20 12v-6a2 2 0 0 0 -2 -2h-12a2 2 0 0 0 -2 2v8" />
                  <path d="M4 18h17" />
                  <path d="M18 15l3 3l-3 3" />
                </svg>
              </div>
            </div>

            {/* Contenido colapsado */}
            <div className="ds-sidebar-content">
              <div className="ds-navigation-menu">
                <button className="ds-nav-item" onClick={() => setSearchModalOpen(true)}>
                  <Search size={16} />
                </button>

                <button
                  className="ds-nav-item"
                  onClick={() => {
                    handleNewChat()
                    if (!sidebarOpen) toggleSidebar()
                  }}
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z"
                    />
                  </svg>
                </button>

                <button
                  className={`ds-nav-item ${activeSection === "proyectos" ? "active" : ""}`}
                  onClick={() => {
                    if (activeSection !== "proyectos") {
                      setActiveSection("proyectos")
                      setProyectosExpanded(true)
                    } else {
                      setProyectosExpanded(!proyectosExpanded)
                    }
                    if (!sidebarOpen) toggleSidebar()
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                </button>

                <button
                  className="ds-nav-item active"
                  onClick={() => {
                    navigate("/artefactos")
                    if (!sidebarOpen) toggleSidebar()
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                </button>

                <button
                  className={`ds-nav-item ${activeSection === "historial" ? "active" : ""}`}
                  onClick={() => {
                    if (activeSection !== "historial") {
                    setActiveSection("historial")
                    setHistorialExpanded(true)
                    setHistorialItemsToShow(10) // Reiniciar paginación
                    } else {
                      setHistorialExpanded(!historialExpanded)
                      if (!historialExpanded) {
                        setHistorialItemsToShow(10) // Reiniciar paginación al abrir
                      }
                    }
                    if (!sidebarOpen) toggleSidebar()
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                    <path d="M12 7v5l4 2" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Footer colapsado */}
            <div className="ds-sidebar-footer">
              <div className="ds-user-section">
                <div className="ds-user-info">
                  <button className="ds-user-circle" onClick={() => {
                    setUserMenuOpen(!userMenuOpen)
                    if (isMobile) {
                      setMobileSidebarOpen(false)
                    }
                  }}>
                    {getUserAvatar()}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="ds-main-content ds-main-content-with-dashboard" ref={mainContentRef}>
        <div className="ds-chat-header">
          {isMobile && (
            <button className="ds-mobile-menu-button" onClick={toggleSidebar}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="24" height="24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
              </svg>
            </button>
          )}
          <div className="ds-chat-header-title">
            <button className="ds-header-option active">
              KPI Articulos
            </button>
          </div>
        </div>


        <div className="ds-chat-layout">
          <div className="dashboard">
            {/* Main Content */}
            <main className="main-content">
              {/* Section 1: Vista Ejecutiva */}
              <section className="section">
                <SectionTitle icon="bar-chart" title="1. Vista Ejecutiva General" color="#10b981" />
                <div className="kpi-grid">
                  <KPICard
                    title="Importe 2025" 
                    value={loadingImporteTotal ? "..." : `€${importeTotal2025.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} 
                    subtitle={!loadingImporteTotal && porcentajeCambioImporte !== 0 ? (
                      <span style={{ color: porcentajeCambioImporte > 0 ? '#10b981' : '#dc2626', fontSize: '0.75rem', fontWeight: 500 }}>
                        {`${porcentajeCambioImporte > 0 ? '+' : ''}${porcentajeCambioImporte.toFixed(1)}% vs año pasado`}
                      </span>
                    ) : null}
                    color="#111827" 
                  />
                  <KPICard
                    title="Articulos Vendidos 2025" 
                    value={loadingArticulosVendidos ? "..." : articulosVendidos.toLocaleString()} 
                    subtitle={!loadingArticulosVendidos && porcentajeCambio !== 0 ? (
                      <span style={{ color: porcentajeCambio > 0 ? '#10b981' : '#dc2626', fontSize: '0.75rem', fontWeight: 500 }}>
                        {`${porcentajeCambio > 0 ? '+' : ''}${porcentajeCambio.toFixed(1)}% vs año pasado`}
                      </span>
                    ) : null}
                    color="#111827" 
                  />
                  <KPICard
                    title="Compras Totales 2025" 
                    value={loadingComprasTotales ? "..." : comprasTotales.toLocaleString()} 
                    subtitle={!loadingComprasTotales && porcentajeCambioCompras !== 0 ? (
                      `${porcentajeCambioCompras > 0 ? '+' : ''}${porcentajeCambioCompras.toFixed(1)}% vs año pasado`
                    ) : null}
                    color="#111827" 
                  />
                  <KPICard
                    title="Margen Total"
                    value="$0"
                    subtitle="0% margen"
                    color="#10b981"
                  />
              </div>
              </section>

              {/* Section 2: Ventas */}
              <section className="section">
                <SectionTitle icon="trending-up" title="2. Dashboard de Ventas" color="#10b981" />
                <div className="kpi-grid kpi-grid-4">
                  <KPICard 
                    title="Ventas del Mes" 
                    value={loadingVentasMes ? "..." : `€${ventasMesActual.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} 
                    subtitle={!loadingVentasMes && porcentajeCambioVentasMes !== 0 ? (
                      <span style={{ color: porcentajeCambioVentasMes > 0 ? '#10b981' : '#dc2626', fontSize: '0.75rem', fontWeight: 500 }}>
                        {`${porcentajeCambioVentasMes > 0 ? '+' : ''}${porcentajeCambioVentasMes.toFixed(1)}% vs mes anterior`}
                      </span>
                    ) : null}
                    color="#111827" 
                  />
                  <KPICard 
                    title="Unidades Vendidas Mes" 
                    value={loadingUnidadesMes ? "..." : unidadesMesActual.toLocaleString()} 
                    color="#111827" 
                  />
                  <KPICard title="Ticket Promedio" value="$0" color="#111827" />
                  <KPICard title="Crecimiento Mensual" value="0%" subtitle="vs mes anterior" color="#10b981" />
                </div>
                {/* Card 1: Evolución de Ventas */}
                  <div className="card">
                  <div className="card-header" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Evolución de Ventas 2025</h3>
                    
                    {/* Filtro de años - Botón elegante */}
                    <div style={{ position: 'relative' }} ref={yearFilterRef}>
                      <button
                        onClick={() => setShowYearFilter(!showYearFilter)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#374151',
                          transition: 'all 0.2s',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3f4f6'
                          e.currentTarget.style.borderColor = '#d1d5db'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#f9fafb'
                          e.currentTarget.style.borderColor = '#e5e7eb'
                        }}
                      >
                        <span>Filtrar años</span>
                        <ChevronDown 
                          size={16} 
                          style={{ 
                            transform: showYearFilter ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                          }} 
                        />
                        {selectedYears.length > 0 && (
                          <span style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '0.125rem 0.5rem',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            minWidth: '20px',
                            textAlign: 'center'
                          }}>
                            {selectedYears.length}
                          </span>
                        )}
                      </button>
                      
                      {/* Dropdown elegante */}
                      {showYearFilter && (
                        <div style={{
                          position: 'absolute',
                          top: 'calc(100% + 0.5rem)',
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                          padding: '1rem',
                          minWidth: '280px',
                          zIndex: 1000,
                          animation: 'fadeIn 0.2s ease-out'
                        }}>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#6b7280',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: '0.75rem',
                            paddingBottom: '0.5rem',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            Seleccionar años
                    </div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '0.5rem',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            paddingRight: '0.25rem'
                          }}>
                            {Array.from({ length: 11 }, (_, i) => 2025 - i).map((year) => (
                              <label
                                key={year}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  cursor: 'pointer',
                                  padding: '0.625rem 0.75rem',
                                  borderRadius: '8px',
                                  backgroundColor: selectedYears.includes(year) ? '#eff6ff' : 'transparent',
                                  border: selectedYears.includes(year) 
                                    ? `1.5px solid ${yearColors[year]}` 
                                    : '1.5px solid transparent',
                                  transition: 'all 0.15s',
                                  position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                  if (!selectedYears.includes(year)) {
                                    e.currentTarget.style.backgroundColor = '#f9fafb'
                                    e.currentTarget.style.borderColor = '#e5e7eb'
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!selectedYears.includes(year)) {
                                    e.currentTarget.style.backgroundColor = 'transparent'
                                    e.currentTarget.style.borderColor = 'transparent'
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedYears.includes(year)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedYears([...selectedYears, year].sort((a, b) => b - a))
                                    } else {
                                      if (selectedYears.length > 1) {
                                        setSelectedYears(selectedYears.filter(y => y !== year))
                                      }
                                    }
                                  }}
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer',
                                    accentColor: yearColors[year],
                                    margin: 0
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: '0.875rem',
                                    fontWeight: selectedYears.includes(year) ? 600 : 500,
                                    color: selectedYears.includes(year) ? '#111827' : '#4b5563',
                                    userSelect: 'none',
                                    flex: 1
                                  }}
                                >
                                  {year}
                      </span>
                                <span
                                  style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: yearColors[year],
                                    display: 'inline-block',
                                    flexShrink: 0
                                  }}
                                />
                              </label>
                            ))}
                          </div>
                          
                          {/* Footer con acciones */}
                          <div style={{
                            marginTop: '0.75rem',
                            paddingTop: '0.75rem',
                            borderTop: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <button
                              onClick={() => {
                                if (selectedYears.length === 11) {
                                  setSelectedYears([2024, 2025])
                                } else {
                                  setSelectedYears(Array.from({ length: 11 }, (_, i) => 2025 - i))
                                }
                              }}
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                color: '#6b7280',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '6px',
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f3f4f6'
                                e.currentTarget.style.color = '#374151'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#6b7280'
                              }}
                            >
                              {selectedYears.length === 11 ? 'Deseleccionar todos' : 'Seleccionar todos'}
                            </button>
                            <button
                              onClick={() => setShowYearFilter(false)}
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: 'white',
                                backgroundColor: '#3b82f6',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0.375rem 0.75rem',
                                borderRadius: '6px',
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#2563eb'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#3b82f6'
                              }}
                            >
                              Aplicar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Chips de años seleccionados */}
                  {selectedYears.length > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '1rem',
                      flexWrap: 'wrap'
                    }}>
                      {selectedYears.sort((a, b) => b - a).map((year) => (
                        <span
                          key={year}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            padding: '0.375rem 0.75rem',
                            backgroundColor: '#eff6ff',
                            border: `1px solid ${yearColors[year]}`,
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#1e40af'
                          }}
                        >
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: yearColors[year],
                              display: 'inline-block'
                            }}
                          />
                          {year}
                          {selectedYears.length > 1 && (
                            <button
                              onClick={() => {
                                if (selectedYears.length > 1) {
                                  setSelectedYears(selectedYears.filter(y => y !== year))
                                }
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                marginLeft: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#6b7280',
                                transition: 'color 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#ef4444'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#6b7280'
                              }}
                            >
                              <X size={12} />
                            </button>
                          )}
                  </span>
                      ))}
                </div>
                  )}

                  {loadingEvolucionVentas ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Cargando datos...</div>
                  ) : (
                    <SalesLineChart data={salesData} selectedYears={selectedYears} yearColors={yearColors} />
                  )}
              </div>

                {/* Card 2: Top 5 Productos */}
                <div className="card" style={{ marginTop: '1.5rem' }}>
                    <div className="card-header">
                      <h3>Top 5 Productos</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '1rem' }}>
                    <div>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '1rem' }}>Artículos Sueltos</h4>
                      <HorizontalBarChart data={topProductosSueltos} showCurrency={false} />
                    </div>
                    <div>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '1rem' }}>Artículos Bandejas</h4>
                      <HorizontalBarChart data={topProductosBandejas} showCurrency={false} />
                    </div>
                </div>
              </div>
            </section>

              {/* Section 3: Compras */}
              <section className="section">
                <SectionTitle icon="shopping-cart" title="3. Dashboard de Compras" color="#10b981" />
                <div className="kpi-grid kpi-grid-4">
                  <KPICard title="Compras del Mes" value="$0" color="#111827" />
                  <KPICard title="Pedidos Realizados" value="0" subtitle="este año" color="#111827" />
                  <KPICard title="Costo Unit. Promedio" value="$0" color="#10b981" />
                  <KPICard title="Proveedor Top" value="-" subtitle="$0 compras" color="#111827" />
                </div>
                <div className="charts-grid">
                  <div className="card">
                <div className="card-header">
                      <h3>Compras por Proveedor</h3>
                  </div>
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Proveedor</th>
                            <th>Compras</th>
                            <th>Pedidos</th>
                            <th>Cumplimiento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {providers.map((item, i) => (
                            <tr key={i}>
                              <td>{item.name}</td>
                              <td className="value-cell">${item.compras.toLocaleString()}</td>
                              <td>{item.pedidos}</td>
                              <td>
                                <div className="progress-cell">
                                  <div className="progress-bar">
                                    <div className="progress-fill" style={{ width: `${item.cumplimiento}%` }}></div>
                </div>
                                  <span>{item.cumplimiento}%</span>
                  </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                </div>
                        </div>
                  <div className="card">
                    <div className="card-header">
                      <h3>Distribución por Categoría</h3>
                    </div>
                    <DonutChart data={categoryData} centerValue="$0" centerLabel="Total" />
                </div>
              </div>
              </section>

              {/* Section 4: Inventario */}
              <section className="section">
                <SectionTitle icon="package" title="4. Control de Inventario" color="#10b981" />
                <div className="kpi-grid kpi-grid-2">
                  <KPICard title="Stock OK" value="0" subtitle="0%" color="#10b981" />
                  <KPICard title="Stock Bajo" value="0" subtitle="0%" color="#111827" />
                </div>
                <div className="card">
                <div className="card-header">
                    <h3>Estado del Inventario por SKU</h3>
                    <div className="status-legend">
                      <button
                        className={`status-item ok ${filtroEstado === null ? 'active' : filtroEstado === 'ok' ? 'active' : ''}`}
                        onClick={() => setFiltroEstado(filtroEstado === 'ok' ? null : 'ok')}
                        style={{ cursor: 'pointer', border: 'none', outline: 'none' }}
                      >
                        OK
                      </button>
                      <button
                        className={`status-item bajo ${filtroEstado === 'bajo' ? 'active' : ''}`}
                        onClick={() => setFiltroEstado(filtroEstado === 'bajo' ? null : 'bajo')}
                        style={{ cursor: 'pointer', border: 'none', outline: 'none' }}
                      >
                        Bajo
                      </button>
                      <button
                        className={`status-item quiebre ${filtroEstado === 'quiebre' ? 'active' : ''}`}
                        onClick={() => setFiltroEstado(filtroEstado === 'quiebre' ? null : 'quiebre')}
                        style={{ cursor: 'pointer', border: 'none', outline: 'none' }}
                      >
                        Quiebre
                      </button>
                      <button
                        className={`status-item sobrestock ${filtroEstado === 'sobrestock' ? 'active' : ''}`}
                        onClick={() => setFiltroEstado(filtroEstado === 'sobrestock' ? null : 'sobrestock')}
                        style={{ cursor: 'pointer', border: 'none', outline: 'none' }}
                      >
                        Sobrestock
                      </button>
                  </div>
                </div>
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Producto</th>
                          <th>Stock</th>
                          <th>Mínimo</th>
                          <th>Máximo</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryData
                          .filter(item => filtroEstado === null || item.estado === filtroEstado)
                          .map((item, i) => (
                          <tr key={i}>
                            <td className="sku-cell">{item.sku}</td>
                            <td>{item.name}</td>
                            <td className={item.estado === "quiebre" ? "alert" : "value-cell"}>{item.stock}</td>
                            <td>{item.minimo}</td>
                            <td>{item.maximo}</td>
                            <td>
                              <span className={`status-badge ${item.estado}`}>
                                {item.estado === 'ok' ? 'OK' : 
                                 item.estado === 'bajo' ? 'Bajo' :
                                 item.estado === 'quiebre' ? 'Quiebre' :
                                 item.estado === 'sobrestock' ? 'Sobrestock' : item.estado}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                </div>
              </div>
            </section>

              {/* Section 5: Alertas */}
              <section className="section">
                <SectionTitle icon="alert-triangle" title="5. Alertas y Notificaciones" color="#10b981" />
                <div className="kpi-grid kpi-grid-4">
                  <KPICard title="Alertas Activas" value="0" color="#dc2626" />
                  <KPICard title="Quiebres Stock" value="0" subtitle="Urgente" color="#dc2626" />
                  <KPICard title="Stock Bajo" value="0" subtitle="Advertencia" color="#111827" />
                  <KPICard title="Sobrestock" value="0" subtitle="Información" color="#111827" />
                </div>
                <div className="card">
              <div className="card-header">
                    <h3>Alertas Críticas</h3>
              </div>
              <div className="table-wrapper">
                    <table className="data-table">
                    <thead>
                    <tr>
                          <th>SKU</th>
                          <th>Producto</th>
                          <th>Stock</th>
                          <th>Detalle</th>
                          <th>Riesgo</th>
                          <th>Acción</th>
                    </tr>
                    </thead>
                    <tbody>
                        {alertsData.map((item, i) => (
                          <tr key={i}>
                            <td className="sku-cell">{item.sku}</td>
                            <td>{item.name}</td>
                            <td className={item.riesgo === "quiebre" ? "alert" : "value-cell"}>{item.stock}</td>
                            <td>
                              {item.riesgo === "quiebre" && `${item.diasQuiebre} días sin stock`}
                              {item.riesgo === "bajo" && `${item.diasCobertura} días cobertura`}
                              {item.riesgo === "sobrestock" && `${item.exceso} unidades exceso`}
                        </td>
                        <td>
                              <span className={`risk-badge ${item.riesgo}`}>{item.riesgo}</span>
                        </td>
                            <td>
                              <span className="action-badge">
                                {item.riesgo === "quiebre" && "Pedir urgente"}
                                {item.riesgo === "bajo" && "Reabastecer"}
                                {item.riesgo === "sobrestock" && "Promocionar"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </div>
                  </div>
            </section>
            </main>
          </div>
          {/* Chat completamente oculto - esta página solo muestra el dashboard */}

          {false && showScrollButton && !isChatEmpty && (
            <div className="ds-scroll-button-container" style={getScrollButtonStyle()}>
              <button className="ds-scroll-to-bottom" onClick={scrollToBottom}>
                <ChevronDown size={16} />
              </button>
            </div>
          )}

          {false && <div className="ds-chat-input-container">
            {/* Input oculto para seleccionar imágenes */}
            <input
              type="file"
              id="chat-image-input"
              accept="image/*"
              onChange={handleImageUploadForChat}
              style={{ display: 'none' }}
            />
            <form onSubmit={handleSubmit}>
              <div className="ds-input-wrapper">
                <div style={{ position: 'relative', width: '100%' }}>
                  {/* Botón para agregar imagen - TEMPORALMENTE OCULTO */}
                  <button
                    type="button"
                    onClick={handleImageSelectForChat}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: selectedImage ? '#e3f2fd' : 'none',
                      border: selectedImage ? '2px solid #2196f3' : 'none',
                      cursor: 'pointer',
                      display: 'none', // OCULTO TEMPORALMENTE
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      color: selectedImage ? '#2196f3' : '#666',
                      transition: 'all 0.2s ease',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = selectedImage ? '#1976d2' : '#333'
                      e.target.style.backgroundColor = selectedImage ? '#bbdefb' : '#f5f5f5'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = selectedImage ? '#2196f3' : '#666'
                      e.target.style.backgroundColor = selectedImage ? '#e3f2fd' : 'transparent'
                    }}
                    title="Agregar imagen (JPG, PNG, GIF - máx. 10MB)"
                  >
                    <CirclePlus size={20} />
                  </button>
                  {/* Interfaz de chat normal */}
                  {(
                    <textarea
                      placeholder={isRecording || isTranscribing ? "" : "¿Cómo puede ayudar Deitana IA?"}
                      value={message}
                      onChange={handleTextareaChange}
                      onKeyDown={handleKeyDown}
                      className="ds-chat-input"
                      disabled={isRecording || isTranscribing}
                      rows={1}
                      style={{
                        backgroundColor: isRecording || isTranscribing ? '#f8f9fa' : 'white',
                        color: 'inherit',
                        paddingLeft: '15px', // Reducido porque el botón de imagen está oculto
                        resize: 'none',
                        overflow: 'hidden',
                        maxHeight: '200px'
                      }}
                    />
                  )}
                  
                  {/* Vista previa de imagen seleccionada */}
                  {selectedImage && (
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '0',
                      right: '0',
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      zIndex: 20
                    }}>
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="Vista previa"
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#333',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {selectedImage.name}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: '#666'
                        }}>
                          {(selectedImage.size / 1024 / 1024).toFixed(1)} MB
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeSelectedImage}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#666',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        title="Eliminar imagen"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                  
                  {/* Overlay de ondas de audio con estilo profesional */}
                  {isRecording && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '50px',
                      gap: '2px',
                      opacity: 1,
                      width: '200px',
                      pointerEvents: 'none'
                    }}>
                      {Array.from({ length: 10 }, (_, index) => {
                        const heights = [12, 18, 25, 15, 28, 20, 16, 22, 18, 14];
                        const delays = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9];
                        const durations = [0.9, 0.7, 1.1, 0.6, 1.3, 0.8, 0.9, 1.0, 0.7, 1.2];
                        
                        return (
                          <div
                            key={index}
                            className={`wave wave-${index + 1}`}
                            style={{
                              width: '3px',
                              height: `${heights[index]}px`,
                              background: 'linear-gradient(to top, #667eea, #764ba2)',
                              borderRadius: '2px',
                              animation: `wave-animation ${durations[index]}s ease-in-out infinite`,
                              animationDelay: `${delays[index]}s`
                            }}
                          />
                        );
                      })}
                      <style jsx>{`
                        @keyframes wave-animation {
                          0%, 100% {
                            transform: scaleY(0.5);
                            opacity: 0.7;
                          }
                          50% {
                            transform: scaleY(1);
                            opacity: 1;
                          }
                        }
                      `}</style>
                    </div>
                  )}
                  
                  {/* Texto de transcripción */}
                  {isTranscribing && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '16px',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      pointerEvents: 'none'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #0066ff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <span style={{
                        fontSize: '14px',
                        color: '#0066ff',
                        fontWeight: '500'
                      }}>
                        🎤 Transcribiendo tu voz a texto...
                      </span>
                      <style jsx>{`
                        @keyframes spin {
                          0% { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                      `}</style>
                    </div>
                  )}
                </div>
                {isRecording || isTranscribing ? (
                  <div style={{ 
                    position: 'absolute', 
                    right: '12px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px' 
                  }}>
                    <button 
                      type="button" 
                      className="ds-send-button"
                      style={{
                        position: 'relative',
                        right: 'auto',
                        bottom: 'auto',
                        backgroundColor: '#ff4444',
                        color: 'white'
                      }}
                      onClick={handleSquareClick}
                      disabled={isTranscribing && !isTranscriptionPaused}
                    >
                      <Square size={18} />
                    </button>
                    <button 
                      type="button" 
                      className="ds-send-button"
                      style={{
                        position: 'relative',
                        right: 'auto',
                        bottom: 'auto'
                      }}
                      onClick={handleArrowUpClick}
                      disabled={!message.trim() && !selectedImage}
                    >
                      <ArrowUp size={18} />
                    </button>
                  </div>
                ) : isTyping ? (
                  <button 
                    type="button" 
                    className="ds-send-button ds-stop-button" 
                    onClick={stopStreaming}
                    title="Detener respuesta"
                  >
                    <CircleStop size={20} />
                  </button>
                ) : (message.trim() || selectedImage) ? (
                  <button 
                    type="submit" 
                    className="ds-send-button" 
                    disabled={false}
                  >
                    <ArrowUp size={20} />
                  </button>
                ) : (
                  <div style={{ 
                    position: 'absolute', 
                    right: '12px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px' 
                  }}>
                    {/* Botón de micrófono - TEMPORALMENTE OCULTO */}
                    <button 
                      type="button" 
                      className="ds-send-button"
                      style={{
                        position: 'relative',
                        right: 'auto',
                        bottom: 'auto',
                        backgroundColor: message.trim() ? '#e3f2fd' : 'transparent',
                        border: message.trim() ? '2px solid #2196f3' : 'none',
                        color: message.trim() ? '#2196f3' : 'inherit',
                        display: 'none' // OCULTO TEMPORALMENTE
                      }}
                      onClick={handleMicClick}
                      title={message.trim() ? "Texto transcrito listo - Haz clic para grabar de nuevo" : "Grabar audio para transcribir"}
                    >
                      <Mic size={18} />
                    </button>
                    <button 
                      type="button" 
                      className="ds-send-button"
                      style={{
                        position: 'relative',
                        right: 'auto',
                        bottom: 'auto',
                        backgroundColor: voiceModalOpen ? '#10a37f' : 'transparent',
                        color: voiceModalOpen ? 'white' : 'inherit'
                      }}
                      onClick={() => setVoiceModalOpen(true)}
                      title="Abrir asistente de voz"
                    >
                      <AudioLines size={18} />
                    </button>
                  </div>
                )}
              </div>
            </form>
            
            <div className="ds-disclaimer">Deitana IA</div>
          </div>}
        </div>
      </div>

      {/* Backdrop */}
      {userMenuOpen && <div className="modal-backdrop" onClick={() => {
          setUserMenuOpen(false)
          // Resetear al menú principal cuando se cierra
          setTimeout(() => setModalView("main"), 300)
        }} />}

      {/* Bottom Sheet para móvil */}
      {isMobile && userMenuOpen && (
        <div
          className={`modal-sheet ${userMenuOpen ? "modal-open" : ""}`}
          style={{
            transform: `translateY(${dragY}px)`,
            transition: isDragging ? "none" : "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          <div className="modal-handle" />

          <div className="modal-content">
            {/* Header con botón de volver atrás si no estamos en el menú principal */}
            {modalView !== "main" && modalView !== "" && modalView && (
              <div className="modal-header-with-back">
                <button className="back-button" onClick={() => setModalView("main")}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5m7-7l-7 7 7 7" />
                  </svg>
                </button>
                <div className="back-button-spacer"></div>
                <div className="back-button-spacer"></div>
              </div>
            )}

            {/* Menú principal */}
            {(modalView === "main" || modalView === "" || !modalView) && (
              <div className="menu-list">
              <div className="menu-item" onClick={() => setModalView("account")}>
                <span className="menu-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <span className="menu-title">Cuenta</span>
                <span className="menu-arrow">›</span>
              </div>
              <div className="menu-item" onClick={() => setModalView("appearance")}>
                <span className="menu-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
                    <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                  </svg>
                </span>
                <span className="menu-title">Configuración</span>
                <span className="menu-arrow">›</span>
              </div>
              <div className="menu-item" onClick={() => setModalView("behavior")}>
                <span className="menu-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                  </svg>
                </span>
                <span className="menu-title">Comportamiento</span>
                <span className="menu-arrow">›</span>
              </div>
              <div className="menu-item" onClick={() => setModalView("personalize")}>
                <span className="menu-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 0 1-1.73V4a2 2 0 0 0-2-2z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </span>
                <span className="menu-title">Personalizar</span>
                <span className="menu-arrow">›</span>
              </div>
              <div className="menu-item" onClick={() => setModalView("data")}>
                <span className="menu-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <rect x="9" y="9" width="6" height="6" />
                    <path d="M9 1v6M15 1v6M9 21v-6M15 21v-6M1 9h6M1 15h6M21 9h-6M21 15h-6" />
                  </svg>
                </span>
                <span className="menu-title">Controles de datos</span>
                <span className="menu-arrow">›</span>
              </div>
              <div className="menu-item" onClick={() => setModalView("apps")}>
                <span className="menu-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <rect x="9" y="9" width="6" height="6" />
                    <path d="M9 1v6M15 1v6M9 21v-6M15 21v-6M1 9h6M1 15h6M21 9h-6M21 15h-6" />
                  </svg>
                </span>
                <span className="menu-title">Apps conectadas</span>
                <span className="menu-arrow">›</span>
              </div>
              <div className="menu-item" onClick={handleLogout}>
                <span className="menu-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m0 14l6-6-6-6m6 6H9" />
                  </svg>
                </span>
                <span className="menu-title">Cerrar sesión</span>
                <span className="menu-arrow">›</span>
              </div>
            </div>
            )}

            {/* Contenido específico de cada sección */}
            {modalView === "account" && (
              <div className="section-content">
                <div className="ds-user-profile-card">
                  <div className="ds-user-avatar-large">
                    {getUserAvatar()}
                  </div>
                  <div className="ds-user-info-large">
                    <div className="ds-user-name-large">{user?.displayName || "Usuario"}</div>
                    <div className="ds-user-email-large">{user?.email || ""}</div>
                  </div>

                </div>
                <div className="ds-id-section">{user?.uid || ""}</div>
              </div>
            )}

            {modalView === "appearance" && (
              <div className="section-content">
                <div className="ds-config-section">
                  <div className="ds-config-item">
                    <label className="ds-config-label">Imagen</label>
                    <div className="ds-profile-image-section">
                      <div className="ds-user-avatar-config">
                        {getUserAvatar()}
                      </div>
                      <button className="ds-edit-image-btn">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                        Editar imagen
                      </button>
                    </div>
                  </div>

                  <div className="ds-config-item">
                    <label className="ds-config-label">Nombre</label>
                    <div className="ds-input-with-button">
                      <input
                        type="text"
                        className="ds-config-input"
                        defaultValue={user?.displayName || ""}
                        placeholder="Ingresa tu nombre"
                      />
                      <button className="ds-input-button">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Sección de contraseña */}
                  <div className="ds-config-item">
                    <label className="ds-config-label">Contraseña</label>
                    <div className="ds-password-section">
                      <div className="ds-input-with-button">
                        <input
                          type="password"
                          className="ds-config-input"
                          defaultValue="password123"
                          placeholder="Ingresa tu contraseña"
                        />
                        <button className="ds-input-button ds-toggle-password-btn">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>
                      <button className="ds-change-password-btn">Ver contraseña</button>
                    </div>
                  </div>

                  <div className="ds-config-actions">
                    <button className="ds-save-config-btn">Guardar cambios</button>
                  </div>
                </div>
              </div>
            )}

            {modalView === "behavior" && (
              <div className="section-content">
                <p>Configuración de comportamiento próximamente...</p>
              </div>
            )}

            {modalView === "personalize" && (
              <div className="section-content">
                <p>Opciones de personalización próximamente...</p>
              </div>
            )}

            {modalView === "data" && (
              <div className="section-content">
                <p>Controles de datos próximamente...</p>
              </div>
            )}

            {modalView === "apps" && (
              <div className="section-content">
                <p>Apps conectadas próximamente...</p>
              </div>
            )}


          </div>
        </div>
      )}

      {/* Modal Desktop */}
      {!isMobile && userMenuOpen && (
        <div className="ds-user-modal-overlay" onClick={() => setUserMenuOpen(false)}>
          <div className="ds-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ds-user-modal-header">
              <h2 className="ds-user-modal-title">
                {modalView === "account"
                  ? "Cuenta"
                  : modalView === "appearance"
                    ? "Configuración"
                    : modalView === "behavior"
                      ? "Comportamiento"
                      : modalView === "personalize"
                        ? "Personalizar"
                        : modalView === "data"
                          ? "Controles de datos"
                          : modalView === "apps"
                            ? "Apps conectadas"
                            : "Configuración"}
              </h2>
              <button
                className="ds-user-modal-close"
                onClick={() => {
                  setUserMenuOpen(false)
                  setModalView("main")
                }}
              >
                ×
              </button>
            </div>

            <div className="ds-user-modal-content">
              <div className="ds-user-modal-sidebar">
                <button
                  className={`ds-modal-nav-item ${(modalView === "account" || modalView === "main") ? "active" : ""}`}
                  onClick={() => setModalView("account")}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span>Cuenta</span>
                </button>

                <button
                  className={`ds-modal-nav-item ${modalView === "appearance" ? "active" : ""}`}
                  onClick={() => setModalView("appearance")}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
                    <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                  </svg>
                  <span>Configuración</span>
                </button>

                <button
                  className={`ds-modal-nav-item ${modalView === "behavior" ? "active" : ""}`}
                  onClick={() => setModalView("behavior")}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                  </svg>
                  <span>Comportamiento</span>
                </button>

                <button
                  className={`ds-modal-nav-item ${modalView === "personalize" ? "active" : ""}`}
                  onClick={() => setModalView("personalize")}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 0 1-1.73V4a2 2 0 0 0-2-2z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <span>Personalizar</span>
                </button>

                <button
                  className={`ds-modal-nav-item ${modalView === "data" ? "active" : ""}`}
                  onClick={() => setModalView("data")}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <rect x="9" y="9" width="6" height="6" />
                    <path d="M9 1v6M15 1v6M9 21v-6M15 21v-6M1 9h6M1 15h6M21 9h-6M21 15h-6" />
                  </svg>
                  <span>Controles de datos</span>
                </button>

                <button
                  className={`ds-modal-nav-item ${modalView === "apps" ? "active" : ""}`}
                  onClick={() => setModalView("apps")}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <rect x="9" y="9" width="6" height="6" />
                    <path d="M9 1v6M15 1v6M9 21v-6M15 21v-6M1 9h6M1 15h6M21 9h-6M21 15h-6" />
                  </svg>
                  <span>Apps conectadas</span>
                </button>
              </div>

              <div className="ds-user-modal-main">
                {(modalView === "account" || modalView === "main" || !modalView) && (
                  <>
                <div className="ds-user-profile-card">
                  <div className="ds-user-avatar-large">
                        {getUserAvatar()}
                  </div>
                  <div className="ds-user-info-large">
                        <div className="ds-user-name-large">{user?.displayName || "Usuario"}</div>
                        <div className="ds-user-email-large">{user?.email || ""}</div>
                  </div>

                </div>

                <div className="ds-language-section">
                  <span className="ds-language-text">Cerrar sesión</span>
                  <button className="ds-admin-btn" onClick={handleLogout}>
                    Cerrar sesión
                  </button>
                </div>

                    <div className="ds-id-section">{user?.uid || ""}</div>
                  </>
                )}



                {modalView === "appearance" && (
                  <div className="ds-config-section">
                    {/* Sección de imagen de perfil */}
                    <div className="ds-config-item">
                      <label className="ds-config-label">Imagen</label>
                      <div className="ds-profile-image-section">
                        <div className="ds-user-avatar-config">
                          {getUserAvatar()}
            </div>
                        <button 
                          className="ds-edit-image-btn"
                          onClick={handleImageSelect}
                          disabled={isUpdatingProfile}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                          </svg>
                          {isUpdatingProfile ? "Subiendo..." : "Editar imagen"}
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                        />
                      </div>
                    </div>

                    {/* Sección de nombre */}
                    <div className="ds-config-item">
                      <label className="ds-config-label">Nombre</label>
                      <div className="ds-input-with-button">
                        <input
                          type="text"
                          className="ds-config-input"
                          value={profileName}
                          onChange={(e) => setProfileName(e.target.value)}
                          placeholder="Ingresa tu nombre"
                          disabled={isUpdatingProfile}
                        />
                        <button className="ds-input-button">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Sección de contraseña */}
                    <div className="ds-config-item">
                      <label className="ds-config-label">Contraseña actual</label>
                      <div className="ds-password-section">
                        <div className="ds-input-with-button">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="ds-config-input"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Contraseña actual"
                            disabled={isUpdatingProfile}
                          />
                          <button 
                            className="ds-input-button ds-toggle-password-btn"
                            onClick={() => setShowPassword(!showPassword)}
                            type="button"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Sección de nueva contraseña */}
                    <div className="ds-config-item">
                      <label className="ds-config-label">Nueva contraseña</label>
                      <div className="ds-password-section">
                        <div className="ds-input-with-button">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="ds-config-input"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Nueva contraseña (opcional)"
                            disabled={isUpdatingProfile}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Mensajes de error y éxito */}
                    {profileError && (
                      <div style={{
                        color: '#dc3545',
                        fontSize: '14px',
                        textAlign: 'center',
                        padding: '8px',
                        backgroundColor: '#f8d7da',
                        borderRadius: '4px',
                        margin: '16px 0'
                      }}>
                        {profileError}
                      </div>
                    )}
                    
                    {profileSuccess && (
                      <div style={{
                        color: '#155724',
                        fontSize: '14px',
                        textAlign: 'center',
                        padding: '8px',
                        backgroundColor: '#d4edda',
                        borderRadius: '4px',
                        margin: '16px 0'
                      }}>
                        {profileSuccess}
                      </div>
                    )}

                    {/* Botón de guardar */}
                    <div className="ds-config-actions">
                      <button 
                        className="ds-save-config-btn"
                        onClick={handleSaveChanges}
                        disabled={isUpdatingProfile}
                      >
                        {isUpdatingProfile ? "Guardando..." : "Guardar cambios"}
                      </button>
                    </div>
                  </div>
                )}

                {modalView === "behavior" && (
                  <div>
                    <p>Configuración de comportamiento próximamente...</p>
                  </div>
                )}

                {modalView === "personalize" && (
                  <div>
                    <p>Opciones de personalización próximamente...</p>
                  </div>
                )}

                {modalView === "data" && (
                  <div>
                    <p>Controles de datos próximamente...</p>
                  </div>
                )}

                {modalView === "apps" && (
                  <div>
                    <p>Apps conectadas próximamente...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Search Modal */}
      {!isMobile && searchModalOpen && (
        <div 
          className="ds-search-modal-overlay"
          onClick={() => {
            setSearchModalOpen(false)
            setSearchQuery("")
            setDebouncedSearchQuery("")
          }}
        >
          <div className="ds-search-modal" onClick={(e) => e.stopPropagation()}>
            {/* Campo de búsqueda fijo */}
            <div className="ds-search-field-container">
              <div className="ds-search-field-wrapper">
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="ds-search-field-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                <Search className="ds-search-field-icon" size={20} />
              </div>
            </div>

            {/* Contenedor scrollable para resultados */}
            <div className="ds-search-results-container">
              {/* Secciones dinámicas usando la misma lógica que el historial */}
              {(() => {
                const filteredChats = filterChatsBySearch(chatHistory, debouncedSearchQuery)
                const groupedChats = groupChatsByDate(filteredChats)

                if (debouncedSearchQuery.trim() && filteredChats.length === 0) {
                  return (
                    <div className="ds-search-section">
                      <div className="ds-search-results">
                        <div className="ds-search-result-item" style={{ cursor: "default", opacity: 0.6 }}>
                          <span className="ds-search-result-title">
                            No se encontraron resultados para "{debouncedSearchQuery}"
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                }

                return Object.entries(groupedChats).map(([dateGroup, chats]) => (
                  <div key={dateGroup} className="ds-search-section">
                    <div className="ds-search-section-header">
                      <span className="ds-search-section-title">{dateGroup}</span>
                    </div>
                    <div className="ds-search-results">
                      {chats.map((chat) => (
                        <div
                          key={chat.id}
                          className="ds-search-result-item"
                          onClick={() => {
                            handleConversationClick(chat.id)
                            setSearchModalOpen(false)
                            setSearchQuery("")
                            setDebouncedSearchQuery("")
                          }}
                        >
                          <span className="ds-search-result-title">
                            {chat.title === "NUEVA_CONEXION" ? "Nueva conversación" : chat.title}
                          </span>
                          <span className="ds-search-result-time">
                            {formatTimeAgo(chat.updatedAt || chat.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Search Bottom Sheet */}
      {isMobile && searchModalOpen && <div className="modal-backdrop" onClick={() => {
          setSearchModalOpen(false)
          setSearchQuery("")
          setDebouncedSearchQuery("")
        }} />}

      {isMobile && searchModalOpen && (
        <div
          className={`modal-sheet ${searchModalOpen ? "modal-open" : ""}`}
          style={{
            transform: `translateY(${searchDragY}px)`,
            transition: isSearchDragging ? "none" : "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
          }}
          onTouchStart={handleSearchTouchStart}
          onTouchMove={handleSearchTouchMove}
          onTouchEnd={handleSearchTouchEnd}
          onMouseDown={handleSearchMouseDown}
        >
          <div className="modal-handle" />

          <div className="modal-content">
            {/* Campo de búsqueda */}
            <div className="search-input-container">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                <Search className="search-input-icon" size={20} />
              </div>
            </div>

            {/* Resultados scrollables */}
            <div className="search-results-scrollable">
              {(() => {
                const filteredChats = filterChatsBySearch(chatHistory, debouncedSearchQuery)
                const groupedChats = groupChatsByDate(filteredChats)

                if (debouncedSearchQuery.trim() && filteredChats.length === 0) {
                  return (
                    <div className="search-section">
                      <div className="search-no-results">
                        <span className="search-no-results-text">
                          No se encontraron resultados para "{debouncedSearchQuery}"
                        </span>
                      </div>
                    </div>
                  )
                }

                return Object.entries(groupedChats).map(([dateGroup, chats]) => (
                  <div key={dateGroup} className="search-section">
                    <div className="search-section-header">
                      <span className="search-section-title">{dateGroup}</span>
                    </div>
                    <div className="search-results">
                      {chats.map((chat) => (
                        <div
                          key={chat.id}
                          className="search-result-item"
                          onClick={() => {
                            handleConversationClick(chat.id)
                            setSearchModalOpen(false)
                            setSearchQuery("")
                            setDebouncedSearchQuery("")
                          }}
                        >
                          <span className="search-result-title">
                            {chat.title === "NUEVA_CONEXION" ? "Nueva conversación" : chat.title}
                          </span>
                          <span className="search-result-time">
                            {formatTimeAgo(chat.updatedAt || chat.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Asistente de Voz */}
      {voiceModalOpen && (
        <VoiceAssistantModal
          isOpen={voiceModalOpen}
          onClose={() => setVoiceModalOpen(false)}
          user={user}
          currentConversationId={currentConversationId}
          setCurrentConversationId={setCurrentConversationId}
          setChatMessages={setChatMessages}
          API_URL={API_URL}
        />
      )}
    </div>
  )
}

export default KPIArticulos












